import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  activateOfferCampaign,
  archiveOfferCampaign,
  createOfferCampaign,
  duplicateOfferCampaign,
  getAdminOfferCampaign,
  listOfferCampaignAnalyticsEmailDeliveries,
  listOfferCampaigns,
  pauseOfferCampaign,
  previewOfferCampaignCode,
  previewOfferCampaignDisplay,
  previewOfferCampaignAnalyticsEmail,
  sendOfferCampaignAnalyticsEmailNow,
  sendOfferCampaignAnalyticsEmailTest,
  testOfferCampaignSource,
  updateOfferCampaign,
  validateOfferCampaign,
  type OfferCampaignAnalyticsEmailDelivery,
} from '../lib/api/offer-campaigns';
import { getLookerSourceFields, listLookerSources, type ConfiguredLookerSourceSummary } from '../lib/api/general-settings';
import type {
  OfferCampaignAdminPayload,
  OfferCampaignAdminSourcePayload,
  OfferCampaignAdminSummary,
  OfferCampaignLookerTestResult,
  OfferCampaignValidationResult,
} from '../types/offer-campaigns';
import { ApiError } from '../lib/api/client';
import type { AdminModalVariant } from './common';
import { OfferCampaignDisplayMonitor } from './OfferCampaignDisplayMonitor';
import {
  OfferCampaignDateInput,
  formatOfferCampaignDate,
  formatOfferCampaignDateTime,
} from './OfferCampaignDateField';

const sourceTypes = ['PATIENT_REGISTRATION', 'ADVANCE_PAYMENT'] as const;
const draftConfigurationReviewKey = 'configurationReview';

function campaignReadiness(campaign: OfferCampaignAdminSummary) {
  const configuredSourceCount = sourceTypes.filter((sourceType) => campaign.sources.some((source) => (
    source.sourceType === sourceType
    && source.isActive
    && source.isConfigured
    && Boolean(source.lookerLookId?.trim())
  ))).length;
  const reviewValue = campaign.themeConfig?.[draftConfigurationReviewKey];
  const review = reviewValue && typeof reviewValue === 'object' && !Array.isArray(reviewValue)
    ? reviewValue as Record<string, unknown>
    : {};
  const previouslyActivated = campaign.status !== 'DRAFT';
  const unlimitedSlots = campaign.themeConfig?.unlimitedSlots === true;
  const hasLimit = unlimitedSlots || Number(campaign.totalSlotLimit) > 0;
  const hasCodePattern = Boolean(campaign.codePattern?.trim());
  const sourcesReady = configuredSourceCount === sourceTypes.length;
  const limitReady = unlimitedSlots || (hasLimit && (previouslyActivated || review.totalSlotLimit === true));
  const codePatternReady = hasCodePattern && (previouslyActivated || review.codePattern === true);

  return {
    configuredSourceCount,
    sourcesReady,
    hasLimit,
    limitReady,
    hasCodePattern,
    codePatternReady,
    complete: sourcesReady && limitReady && codePatternReady,
  };
}

type MappingField = { key: string; label: string };
const patientMappingKeys: MappingField[] = [
  { key: 'mrn', label: 'Patient MRN' }, { key: 'patient_name', label: 'Patient Name' }, { key: 'female_partner_name', label: 'Female Partner Name' }, { key: 'primary_doctor_name', label: 'Primary Doctor Name' }, { key: 'b2bb2c', label: 'B2B / B2C' }, { key: 'date_of_birth', label: 'Date of Birth' }, { key: 'age', label: 'Patient Age' }, { key: 'center', label: 'Patient Center' }, { key: 'registration_timestamp', label: 'Registration Timestamp' },
];
const advanceMappingKeys: MappingField[] = [
  { key: 'advance_mrn', label: 'Patient MRN' },
  { key: 'advance_center', label: 'Payment Center' },
  { key: 'advance_code', label: 'Offer Code / Payment Instructions' },
  { key: 'payment_amount', label: 'Advance Payment Amount' },
  { key: 'payment_date', label: 'Payment Date' },
  { key: 'package_start_date', label: 'Package Start Date' },
  { key: 'package_end_date', label: 'Package End Date' },
  { key: 'package_plan', label: 'Enrolled Plan Name' },
  { key: 'package_plan_flag', label: 'Is Package Plan' },
  { key: 'plan_fee', label: 'Original Plan Fee' },
  { key: 'discount_applied', label: 'Discount Applied' },
  { key: 'total_amount_due', label: 'Total Amount Due' },
  { key: 'escrow_status', label: 'Escrow Status' },
  { key: 'item_status', label: 'Payment Item Status' },
  { key: 'payment_mode', label: 'Payment Mode' },
  { key: 'card_type', label: 'Card Type (Optional)' },
  { key: 'payment_reference', label: 'Payment Reference (Optional)' },
  { key: 'cheque_number', label: 'Cheque Number (Optional)' },
];
const celebrationOptions = ['NONE', 'SPARKLE', 'CONFETTI', 'SPARKLE_CONFETTI', 'FIREWORKS_LIGHT'];

type CampaignEditorPanel = 'identity' | 'sources' | 'mapping' | 'rules' | 'verification' | 'display' | 'coupon' | 'analyticsEmail' | 'validate';
const campaignEditorPanels: Array<{ key: CampaignEditorPanel; label: string; help: string }> = [
  { key: 'identity', label: 'Workflow Identity', help: 'Name, key, route and status' },
  { key: 'sources', label: 'Looker Sources', help: 'Patient and payment Looks' },
  { key: 'mapping', label: 'Field Mapping', help: 'Map Looker fields' },
  { key: 'rules', label: 'Eligibility Rules', help: 'Slots and discount' },
  { key: 'verification', label: 'Advance Verification', help: 'Payment match rules' },
  { key: 'display', label: 'Code And Display', help: 'Code, route and TV board' },
  { key: 'coupon', label: 'Coupon & Terms', help: 'Coupon wording, terms and acknowledgement' },
  { key: 'analyticsEmail', label: 'Analytics Email', help: 'Recipients, timing and template data' },
  { key: 'validate', label: 'Validate', help: 'Preview and activate' },
];

export function OfferCampaignWorkflowSettings({
  active,
  canUpdate,
  centers,
  notify,
  refreshSignal = 0,
  onViewAudit,
}: {
  active: boolean;
  canUpdate: boolean;
  centers?: string[];
  notify?: (message: string, type?: AdminModalVariant, title?: string) => void;
  refreshSignal?: number;
  onViewAudit?: (campaignKey: string) => void;
}) {
  const [campaigns, setCampaigns] = useState<OfferCampaignAdminSummary[]>([]);
  const [lookerSources, setLookerSources] = useState<ConfiguredLookerSourceSummary[]>([]);
  const [editor, setEditor] = useState<{ mode: 'add' | 'edit'; campaign?: OfferCampaignAdminSummary } | null>(null);
  const [draft, setDraft] = useState<OfferCampaignAdminPayload & { campaignKey?: string }>(emptyDraft());
  const [validation, setValidation] = useState<OfferCampaignValidationResult | null>(null);
  const [sourceTests, setSourceTests] = useState<Record<string, OfferCampaignLookerTestResult>>({});
  const [preview, setPreview] = useState<unknown>(null);
  const [busy, setBusy] = useState('');
  const [saveIssues, setSaveIssues] = useState<string[]>([]);
  const [editorInputIssues, setEditorInputIssues] = useState<Record<string, string>>({});
  const [editorBaseline, setEditorBaseline] = useState('');
  const [monitorCampaignId, setMonitorCampaignId] = useState<string | null>(null);
  const draftRef = useRef(draft);
  const editorRef = useRef(editor);
  const editorInputIssuesRef = useRef(editorInputIssues);
  draftRef.current = draft;
  editorRef.current = editor;
  editorInputIssuesRef.current = editorInputIssues;

  const load = useCallback(async () => {
    if (!active) return;
    const [campaignRows, lookRows] = await Promise.all([
      listOfferCampaigns().catch(() => []),
      listLookerSources().catch(() => []),
    ]);
    setCampaigns(campaignRows);
    setLookerSources(lookRows);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    void load();
  }, [active, load, refreshSignal]);

  const openAdd = () => {
    setValidation(null);
    setSourceTests({});
    setPreview(null);
    setSaveIssues([]);
    setEditorInputIssues({});
    editorInputIssuesRef.current = {};
    const nextDraft = emptyDraft();
    const nextEditor = { mode: 'add' as const };
    draftRef.current = nextDraft;
    editorRef.current = nextEditor;
    setDraft(nextDraft);
    setEditorBaseline(campaignDraftFingerprint(nextDraft));
    setEditor(nextEditor);
  };

  const openEdit = async (campaign: OfferCampaignAdminSummary) => {
    setBusy('Open campaign workflow');
    setValidation(null);
    setSourceTests({});
    setPreview(null);
    setSaveIssues([]);
    setEditorInputIssues({});
    editorInputIssuesRef.current = {};
    try {
      // The list can be older than the stored campaign. Always establish the
      // editor baseline from a fresh detail response so an ordinary first save
      // does not fail optimistic concurrency validation.
      const latestCampaign = await getAdminOfferCampaign(campaign.id);
      const nextDraft = draftFromCampaign(latestCampaign);
      const nextEditor = { mode: 'edit' as const, campaign: latestCampaign };
      draftRef.current = nextDraft;
      editorRef.current = nextEditor;
      setDraft(nextDraft);
      setEditorBaseline(campaignDraftFingerprint(nextDraft));
      setEditor(nextEditor);
    } catch (err) {
      notify?.(`Open campaign workflow failed: ${errorMessage(err)}`, 'error');
    } finally {
      setBusy('');
    }
  };

  const changeDraft = (nextDraft: OfferCampaignAdminPayload & { campaignKey?: string }) => {
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    setValidation(null);
    setSaveIssues([]);
  };

  const closeEditor = (force = false) => {
    const currentEditor = editorRef.current;
    const hasUnsavedChanges = currentEditor
      ? campaignDraftFingerprint(draftRef.current) !== editorBaseline
      : false;
    if (!force && hasUnsavedChanges && !window.confirm('Discard the unsaved campaign workflow changes?')) return;
    editorRef.current = null;
    setEditor(null);
    setDraft(emptyDraft());
    setEditorInputIssues({});
    editorInputIssuesRef.current = {};
    setSaveIssues([]);
    setValidation(null);
    setSourceTests({});
    setPreview(null);
  };

  const runAction = async (label: string, action: () => Promise<unknown>) => {
    setBusy(label);
    try {
      await action();
      notify?.(`${label} completed`, 'success');
      await load();
    } catch (err) {
      notify?.(`${label} failed: ${errorMessage(err)}`, 'error');
    } finally {
      setBusy('');
    }
  };

  const applyEditorCampaign = (campaign: OfferCampaignAdminSummary) => {
    const nextDraft = draftFromCampaign(campaign);
    const nextEditor = { mode: 'edit' as const, campaign };
    draftRef.current = nextDraft;
    editorRef.current = nextEditor;
    setDraft(nextDraft);
    setEditor(nextEditor);
    setEditorBaseline(campaignDraftFingerprint(nextDraft));
    setValidation(null);
  };

  const runEditorStatusAction = async (label: string, action: () => Promise<OfferCampaignAdminSummary>) => {
    setBusy(label);
    try {
      const updated = await action();
      applyEditorCampaign(updated);
      notify?.(`${label} completed`, 'success');
      await load();
    } catch (err) {
      notify?.(`${label} failed: ${errorMessage(err)}`, 'error');
    } finally {
      setBusy('');
    }
  };

  const runInlineAction = async (label: string, action: () => Promise<unknown>) => {
    setBusy(label);
    try {
      await action();
    } catch (err) {
      notify?.(`${label} failed: ${errorMessage(err)}`, 'error');
    } finally {
      setBusy('');
    }
  };

  const persistDraft = async (currentDraft: OfferCampaignAdminPayload & { campaignKey?: string }) => {
    const currentEditor = editorRef.current;
    if (!currentEditor) throw new Error('Campaign editor is no longer open. Reopen it and try again.');
    const clientIssues = Object.values(editorInputIssuesRef.current).filter(Boolean);
    if (clientIssues.length > 0) throw new Error(clientIssues.join('; '));

    const requestedPayload = normalizeDraft(currentDraft);
    let persistedPayload = requestedPayload;
    let saved: OfferCampaignAdminSummary;
    if (currentEditor.mode === 'add') {
      saved = await createOfferCampaign({
        ...requestedPayload,
        campaignKey: String(currentDraft.campaignKey || '').trim().toUpperCase(),
      });
    } else {
      const campaignId = currentEditor.campaign?.id || '';
      const baselinePayload = normalizeDraft(draftFromCampaign(currentEditor.campaign!));
      let latestCampaign = await getAdminOfferCampaign(campaignId);

      const saveAgainstLatest = async () => {
        const merge = mergeCampaignPayload(
          baselinePayload,
          requestedPayload,
          normalizeDraft(draftFromCampaign(latestCampaign)),
        );
        if (merge.conflicts.length > 0) {
          throw new Error(
            `Another administrator changed the same field(s): ${merge.conflicts.join(', ')}. `
            + 'Your entries are still present. Review those fields and save again.',
          );
        }
        persistedPayload = merge.value;
        return updateOfferCampaign(campaignId, {
          ...persistedPayload,
          expectedUpdatedAt: latestCampaign.updatedAt,
        });
      };

      try {
        saved = await saveAgainstLatest();
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 409) throw err;
        // A write landed between the pre-save refresh and PUT. Refresh and
        // safely rebase once more; same-field conflicts still remain blocked.
        latestCampaign = await getAdminOfferCampaign(campaignId);
        saved = await saveAgainstLatest();
      }
    }
    const reloaded = await getAdminOfferCampaign(saved.id);
    const persistenceIssues = campaignPersistenceIssues(persistedPayload, reloaded);
    if (persistenceIssues.length > 0) {
      throw new Error(`The server did not confirm every saved field: ${persistenceIssues.join('; ')}`);
    }
    return reloaded;
  };

  const saveDraft = async () => {
    if (!canUpdate) return;
    setSaveIssues([]);
    setBusy('Save campaign workflow');
    try {
      const saved = await persistDraft(draftRef.current);
      notify?.('Offer campaign workflow saved and verified', 'success');
      setCampaigns((current) => current.map((campaign) => campaign.id === saved.id ? saved : campaign));
      await load();
      closeEditor(true);
    } catch (err) {
      setSaveIssues(errorIssues(err));
    } finally {
      setBusy('');
    }
  };

  const selectedEditorId = editor?.campaign?.id;
  const editorHasUnsavedChanges = Boolean(editor)
    && campaignDraftFingerprint(draft) !== editorBaseline;

  return (
    <>
      {campaigns.map((campaign) => {
        const readiness = campaignReadiness(campaign);
        return (
        <tr key={`offer-campaign-${campaign.id}`} className="offer-workflow-row">
          <td><strong>{campaign.campaignName}</strong><small>{campaign.themeConfig?.description ? String(campaign.themeConfig.description) : 'Offer campaign workflow'} • {campaign.campaignKey}</small></td>
          <td>
            {campaign.sources.map((source) => (
              <small key={source.sourceType}>{source.sourceType}: Look {source.lookerLookId || '-'} {source.isConfigured ? 'Mapped' : 'Needs mapping'}</small>
            ))}
          </td>
          <td><strong>{readiness.complete ? 'Ready for validation' : 'Setup required'}</strong><small>{readiness.complete ? 'All required configuration is present' : 'Open Edit and complete the highlighted items'}</small></td>
          <td>
            OFFER CAMPAIGN
            <div className="workflow-readiness-strip">
              <span className={readiness.sourcesReady ? 'ready' : 'missing'}>Sources {readiness.sourcesReady ? '2/2 configured' : `${readiness.configuredSourceCount}/2 configured`}</span>
              <span className={readiness.limitReady ? 'ready' : readiness.hasLimit ? 'review' : 'missing'}>Limit {readiness.limitReady ? 'configured' : readiness.hasLimit ? 'needs review' : 'missing'}</span>
              <span className={readiness.codePatternReady ? 'ready' : readiness.hasCodePattern ? 'review' : 'missing'}>Code pattern {readiness.codePatternReady ? 'confirmed' : readiness.hasCodePattern ? 'needs review' : 'missing'}</span>
            </div>
          </td>
          <td><strong>{campaign.themeConfig?.unlimitedSlots === true ? 'Unlimited' : campaign.totalSlotLimit}</strong><small>{campaign.themeConfig?.unlimitedSlots === true ? 'capacity' : 'slots'}</small></td>
          <td><span className={`status-chip ${campaign.status === 'ACTIVE' ? 'status-good' : 'status-muted'}`}>{campaign.status}</span></td>
          <td>-</td>
          <td><strong>system</strong><small>{formatOfferCampaignDateTime(campaign.updatedAt)}</small></td>
          <td>
            <div className="row-actions">
              <button className="mini-btn pink" disabled={busy !== ''} onClick={() => void openEdit(campaign)}>{canUpdate ? 'Edit' : 'View'}</button>
              <button className="mini-btn" disabled={!canUpdate || busy !== ''} onClick={() => void runAction('Duplicate campaign workflow', () => duplicateOfferCampaign(campaign.id))}>Copy</button>
              <button className="mini-btn" disabled={!canUpdate || busy !== ''} onClick={() => void runAction('Validate campaign workflow', () => validateOfferCampaign(campaign.id))}>Validate</button>
              <button className="mini-btn green" disabled={!canUpdate || busy !== '' || campaign.status === 'ACTIVE'} onClick={() => void runAction('Activate campaign workflow', () => activateOfferCampaign(campaign.id))}>Activate</button>
              <button className="mini-btn" disabled={!canUpdate || busy !== '' || campaign.status !== 'ACTIVE'} onClick={() => void runAction('Pause campaign workflow', () => pauseOfferCampaign(campaign.id))}>Deactivate</button>
              <button className="mini-btn" disabled={!canUpdate || busy !== ''} onClick={() => window.confirm(`Archive ${campaign.campaignName}?`) && void runAction('Archive campaign workflow', () => archiveOfferCampaign(campaign.id))}>Delete</button>
              <button className="mini-btn" onClick={() => onViewAudit?.(campaign.campaignKey)}>View Audit</button>
            </div>
          </td>
        </tr>
        );
      })}
      {editor && (
        <CampaignWorkflowEditorModal
          mode={editor.mode}
          campaignId={selectedEditorId || ''}
          draft={draft}
          canUpdate={canUpdate}
          busy={busy}
          centers={centers || []}
          lookerSources={lookerSources}
          sourceTests={sourceTests}
          validation={validation}
          preview={preview}
          onClosePreview={() => setPreview(null)}
          saveIssues={saveIssues}
          hasUnsavedChanges={editorHasUnsavedChanges}
          inputIssues={Object.values(editorInputIssues).filter(Boolean)}
          onInputIssue={(key, issue) => setEditorInputIssues((current) => {
            if (!issue) {
              const next = { ...current };
              delete next[key];
              editorInputIssuesRef.current = next;
              return next;
            }
            const next = { ...current, [key]: issue };
            editorInputIssuesRef.current = next;
            return next;
          })}
          onChange={changeDraft}
          onClose={() => closeEditor()}
          onSave={saveDraft}
          onValidate={async () => {
            if (!selectedEditorId || editorHasUnsavedChanges) return;
            await runInlineAction('Validate workflow', async () => setValidation(await validateOfferCampaign(selectedEditorId)));
          }}
          onActivate={async () => {
            if (!selectedEditorId || editorHasUnsavedChanges) return;
            await runEditorStatusAction('Activate campaign workflow', () => activateOfferCampaign(selectedEditorId));
          }}
          onDeactivate={async () => {
            if (!selectedEditorId || editorHasUnsavedChanges) return;
            await runEditorStatusAction('Deactivate campaign workflow', () => pauseOfferCampaign(selectedEditorId));
          }}
          onPreviewCode={async () => {
            if (!selectedEditorId || editorHasUnsavedChanges) return;
            await runInlineAction('Preview code', async () => setPreview(await previewOfferCampaignCode(selectedEditorId)));
          }}
          onPreviewDisplay={async () => {
            if (!selectedEditorId || editorHasUnsavedChanges) return;
            await runInlineAction('Preview display', async () => setPreview(await previewOfferCampaignDisplay(selectedEditorId)));
          }}
          onPreviewAnalyticsEmail={async () => {
            if (!selectedEditorId || editorHasUnsavedChanges) return;
            await runInlineAction('Preview analytics email', async () => {
              setPreview(await previewOfferCampaignAnalyticsEmail(selectedEditorId));
            });
          }}
          onSendAnalyticsEmailTest={async (recipient) => {
            if (!selectedEditorId || editorHasUnsavedChanges) return;
            await runInlineAction('Send analytics test email', async () => {
              setPreview(await sendOfferCampaignAnalyticsEmailTest(selectedEditorId, recipient));
            });
          }}
          onSendAnalyticsEmailNow={async () => {
            if (!selectedEditorId || editorHasUnsavedChanges || !window.confirm('Send the current campaign analytics report to all configured recipients now?')) return;
            await runInlineAction('Send analytics email now', async () => {
              setPreview(await sendOfferCampaignAnalyticsEmailNow(selectedEditorId));
            });
          }}
          onTestSource={async (sourceType) => {
            if (!selectedEditorId || editorHasUnsavedChanges) return;
            const source = draft.sources?.find((item) => item.sourceType === sourceType);
            const label = sourceType === 'PATIENT_REGISTRATION' ? 'Test patient source' : 'Test advance source';
            await runInlineAction(label, async () => {
              try {
                const result = await testOfferCampaignSource(selectedEditorId, sourceType);
                const finalResult = !result.success && result.fields.length === 0
                  ? await fallbackLookerSourceFields(sourceType, source, lookerSources, result.error || 'Offer source preview failed.')
                  : result;
                setSourceTests((current) => ({ ...current, [sourceType]: finalResult }));
              } catch (error) {
                const finalResult = await fallbackLookerSourceFields(sourceType, source, lookerSources, apiErrorMessage(error));
                setSourceTests((current) => ({
                  ...current,
                  [sourceType]: finalResult,
                }));
              }
            });
          }}
          onOpenDisplayMonitor={() => editor?.campaign?.id && setMonitorCampaignId(editor.campaign.id)}
        />
      )}
      {monitorCampaignId && <OfferCampaignDisplayMonitor campaignId={monitorCampaignId} onClose={() => setMonitorCampaignId(null)} />}
    </>
  );
}

function apiErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return 'Looker source preview failed.';
}

async function fallbackLookerSourceFields(
  sourceType: string,
  source: OfferCampaignAdminSourcePayload | undefined,
  lookerSources: ConfiguredLookerSourceSummary[],
  originalError: string,
): Promise<OfferCampaignLookerTestResult> {
  const lookerLookId = String(source?.lookerLookId ?? '').trim();
  const configuredLook = lookerSources.find((item) => String(item.lookId ?? '') === lookerLookId);
  if (!configuredLook) {
    return {
      success: false,
      sourceType,
      lookerLookId,
      rowCount: 0,
      fields: [],
      error: `${originalError}. No matching Integration Settings Look was found for Look ID ${lookerLookId || '-'}.`,
    };
  }

  try {
    const preview = await getLookerSourceFields(configuredLook.key);
    return {
      success: preview.success,
      sourceType,
      lookerLookId,
      rowCount: preview.rowCount,
      fields: preview.fields.map((field) => ({
        fieldKey: field.fieldKey,
        fieldLabel: field.fieldLabel,
        sampleValue: field.sampleValue,
      })),
      maskedSamples: preview.maskedSamples,
      error: preview.success
        ? `Offer source preview failed: ${originalError}. Field list loaded from Integration Looker Look "${configuredLook.name}".`
        : `Offer source preview failed: ${originalError}. Integration Looker preview also failed.`,
    };
  } catch (error) {
    return {
      success: false,
      sourceType,
      lookerLookId,
      rowCount: 0,
      fields: [],
      error: `${originalError}. Integration Looker fallback failed: ${apiErrorMessage(error)}`,
    };
  }
}

function CampaignWorkflowEditorModal({
  mode,
  campaignId,
  draft,
  canUpdate,
  busy,
  centers,
  lookerSources,
  sourceTests,
  validation,
  preview,
  onClosePreview,
  saveIssues,
  hasUnsavedChanges,
  inputIssues,
  onInputIssue,
  onChange,
  onClose,
  onSave,
  onValidate,
  onActivate,
  onDeactivate,
  onPreviewCode,
  onPreviewDisplay,
  onPreviewAnalyticsEmail,
  onSendAnalyticsEmailTest,
  onSendAnalyticsEmailNow,
  onTestSource,
  onOpenDisplayMonitor,
}: {
  mode: 'add' | 'edit';
  campaignId: string;
  draft: OfferCampaignAdminPayload & { campaignKey?: string };
  canUpdate: boolean;
  busy: string;
  centers: string[];
  lookerSources: ConfiguredLookerSourceSummary[];
  sourceTests: Record<string, OfferCampaignLookerTestResult>;
  validation: OfferCampaignValidationResult | null;
  preview: unknown;
  onClosePreview: () => void;
  saveIssues: string[];
  hasUnsavedChanges: boolean;
  inputIssues: string[];
  onInputIssue: (key: string, issue: string) => void;
  onChange: (draft: OfferCampaignAdminPayload & { campaignKey?: string }) => void;
  onClose: () => void;
  onSave: () => Promise<void>;
  onValidate: () => void | Promise<void>;
  onActivate: () => void | Promise<void>;
  onDeactivate: () => void | Promise<void>;
  onPreviewCode: () => void | Promise<void>;
  onPreviewDisplay: () => void | Promise<void>;
  onPreviewAnalyticsEmail: () => void | Promise<void>;
  onSendAnalyticsEmailTest: (recipient: string) => void | Promise<void>;
  onSendAnalyticsEmailNow: () => void | Promise<void>;
  onTestSource: (sourceType: string) => void | Promise<void>;
  onOpenDisplayMonitor: () => void;
}) {
  const [activePanel, setActivePanel] = useState<CampaignEditorPanel>('identity');
  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) => onChange({ ...draft, [key]: value });
  const theme = draft.themeConfig || {};
  const setReviewed = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K], reviewKey: 'totalSlotLimit' | 'codePattern') => onChange({
    ...draft,
    [key]: value,
    themeConfig: {
      ...theme,
      configurationReview: {
        ...normalizeObject(theme.configurationReview),
        [reviewKey]: true,
      },
    },
  });
  const couponValidityIsFixedDate = String(theme.couponValidityUnit || 'DAYS') === 'FIXED_DATE';
  const overallPatientMode = String(theme.patientEligibilityMode || (
    theme.registrationDateFrom || theme.registrationDateTo ? 'NEW_REGISTRATION_RANGE' : 'ANY'
  ));
  const campaignCenterOptions = eligibleCenterOptions(draft, centers || []);
  const setTheme = (key: string, value: unknown) => onChange({ ...draft, themeConfig: { ...theme, [key]: value } });
  const setCouponValidityUnit = (value: string) => {
    const nextTheme: Record<string, unknown> = { ...theme, couponValidityUnit: value };
    if (value === 'FIXED_DATE') {
      delete nextTheme.couponValidityDuration;
    } else {
      delete nextTheme.couponValidityFixedAt;
    }
    onChange({ ...draft, themeConfig: nextTheme });
  };
  const activeIndex = campaignEditorPanels.findIndex((panel) => panel.key === activePanel);
  const canGoBack = activeIndex > 0;
  const canGoNext = activeIndex < campaignEditorPanels.length - 1;
  const goBack = () => canGoBack && setActivePanel(campaignEditorPanels[activeIndex - 1].key);
  const goNext = () => canGoNext && setActivePanel(campaignEditorPanels[activeIndex + 1].key);
  const workflowIsActive = draft.status === 'ACTIVE';
  const statusActionLabel = workflowIsActive ? 'Deactivate Workflow' : 'Activate Workflow';
  const statusActionLoadingLabel = workflowIsActive ? 'Deactivating...' : 'Activating...';
  const statusActionBusyLabel = workflowIsActive ? 'Deactivate campaign workflow' : 'Activate campaign workflow';
  const storedActionDisabled = mode === 'add' || !canUpdate || busy !== '' || hasUnsavedChanges || inputIssues.length > 0;

  useEffect(() => {
    if (saveIssues.length > 0) {
      setActivePanel('validate');
    }
  }, [saveIssues.length]);

  const sections: Record<CampaignEditorPanel, ReactNode> = {
    identity: (
      <EditorSection title="Workflow Identity" help="Name, key, routes, dates and lifecycle status">
        {mode === 'add' && <TextField label="Workflow Key" value={draft.campaignKey || ''} disabled={!canUpdate || busy !== ''} onChange={(value) => set('campaignKey', value.toUpperCase())} />}
        <TextField label="Workflow Name" value={draft.campaignName} disabled={!canUpdate || busy !== ''} onChange={(value) => set('campaignName', value)} />
        <TextField label="Enrollment Route Slug" value={draft.routeSlug} disabled={!canUpdate || busy !== ''} onChange={(value) => set('routeSlug', value)} />
        <TextField label="Display Route Slug" value={draft.displayRouteSlug} disabled={!canUpdate || busy !== ''} onChange={(value) => set('displayRouteSlug', value)} />
        {mode === 'edit' && canUpdate && <div className="offer-config-source-box"><strong>Display Board Monitor</strong><small>{hasUnsavedChanges ? 'Save this workflow before opening the monitor so it uses the latest configuration.' : 'View active TV/browser connections, route version, refresh health, IP address and last served campaign totals.'}</small><button type="button" className="mini-btn pink" disabled={storedActionDisabled} onClick={onOpenDisplayMonitor}>Open Monitor</button></div>}
        <TextField label="Status" value={draft.status || 'DRAFT'} disabled onChange={() => undefined} help="Use Validate and Activate/Deactivate for lifecycle changes. Status cannot be bypassed through Save." />
        <TextField label="Start Date/Time (IST)" type="datetime-local" value={toIstInput(draft.startsAt)} disabled={!canUpdate || busy !== ''} onChange={(value) => set('startsAt', value ? istInputToIso(value) : null)} />
        <TextField label="End Date/Time (IST)" type="datetime-local" value={toIstInput(draft.endsAt)} disabled={!canUpdate || busy !== ''} onChange={(value) => set('endsAt', value ? istInputToIso(value) : null)} />
        <TextField label="Description" value={String(theme.description || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('description', value)} />
      </EditorSection>
    ),
    sources: (
      <EditorSection title="Looker Sources" help="Dynamic patient and payment source configuration. Select the Integration Settings Looks required by this campaign.">
        {sourceTypes.map((type) => {
          const source = sourceFor(draft, type);
          const test = sourceTests[type];
          const sourceBusyLabel = type === 'PATIENT_REGISTRATION' ? 'Test patient source' : 'Test advance source';
          const sourceBusy = busy === sourceBusyLabel;
          return (
            <div key={type} className="offer-config-source-box">
              <strong>{type === 'PATIENT_REGISTRATION' ? 'Patient Registration Source' : 'Advance Payment Source'}</strong>
              <SelectField label="Configured Integration Look" value={source.lookerLookId} disabled={!canUpdate || busy !== ''} options={['', ...lookerSources.map((item) => String(item.lookId || ''))]} optionLabels={lookerSources.reduce<Record<string, string>>((acc, item) => ({ ...acc, [String(item.lookId || '')]: `${item.name} (${item.lookId})` }), { '': 'Manual / not selected' })} onChange={(value) => updateSource(draft, onChange, type, { lookerLookId: value })} />
              <TextField label="Manual Look ID" value={source.lookerLookId} disabled={!canUpdate || busy !== '' || lookerSources.length > 0} onChange={(value) => updateSource(draft, onChange, type, { lookerLookId: value })} />
              <TextField label="Source Name" value={source.sourceName} disabled={!canUpdate || busy !== ''} onChange={(value) => updateSource(draft, onChange, type, { sourceName: value })} />
              <ToggleField label="Source Active" value={source.isActive !== false} disabled={!canUpdate || busy !== ''} onChange={(value) => updateSource(draft, onChange, type, { isActive: value })} />
              <ActionButton
                className="mini-btn"
                label="Test And Preview Fields"
                loadingLabel="Testing Fields..."
                loading={sourceBusy}
                disabled={storedActionDisabled}
                onClick={() => onTestSource(type)}
              />
              {test && <pre className="offer-admin-preview">{JSON.stringify({
                success: test.success,
                rowCount: test.rowCount,
                fields: test.fields.slice(0, 20),
                warning: test.warning,
                error: test.error,
              }, null, 2)}</pre>}
            </div>
          );
        })}
      </EditorSection>
    ),
    mapping: (
      <EditorSection title="Field Mapping" help="Map campaign-required fields to selected Looker fields. No field name should be hardcoded in service logic.">
        <MappingEditor title="Patient Registration Fields" type="PATIENT_REGISTRATION" keys={patientMappingKeys} draft={draft} tests={sourceTests} onChange={onChange} disabled={!canUpdate || busy !== ''} />
        <MappingEditor title="Advance Payment Fields" type="ADVANCE_PAYMENT" keys={advanceMappingKeys} draft={draft} tests={sourceTests} onChange={onChange} disabled={!canUpdate || busy !== ''} />
      </EditorSection>
    ),
    rules: (
      <EditorSection title="Eligibility, Limit And Discount" help="Dynamic business rules for any offer campaign">
        <ToggleField label="Unlimited Slots" value={theme.unlimitedSlots === true} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('unlimitedSlots', value)} />
        {theme.unlimitedSlots !== true && <NumberField label="Total Slot Limit" value={draft.totalSlotLimit} min={1} max={100000} integer disabled={!canUpdate || busy !== ''} onChange={(value) => setReviewed('totalSlotLimit', value, 'totalSlotLimit')} help="Review and save the intended campaign capacity. The initial value is only a Draft placeholder." />}
        <SelectField
          label="Discount Formula"
          value={draft.discountFormulaType}
          disabled={!canUpdate || busy !== ''}
          options={['AGE_MULTIPLIER', 'FIXED_AMOUNT', 'PERCENT_OF_PACKAGE', 'CUSTOM_RULE']}
          optionLabels={{
            AGE_MULTIPLIER: 'Age Multiplier',
            FIXED_AMOUNT: 'Fixed Amount',
            PERCENT_OF_PACKAGE: 'Percent Of Package (not available)',
            CUSTOM_RULE: 'Custom Rule (not available)',
          }}
          disabledOptions={['PERCENT_OF_PACKAGE', 'CUSTOM_RULE']}
          onChange={(value) => set('discountFormulaType', value)}
        />
        {draft.discountFormulaType === 'FIXED_AMOUNT' ? (
          <NumberField label="Fixed Discount Amount (₹)" value={Number(theme.fixedDiscountAmount || 0)} min={0} step={0.01} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('fixedDiscountAmount', value > 0 ? value : null)} help="The exact discount granted to every eligible patient." />
        ) : (
          <NumberField label="Age Multiplier" value={draft.ageMultiplier} min={0} step={0.01} disabled={!canUpdate || busy !== ''} onChange={(value) => set('ageMultiplier', value)} />
        )}
        <NumberField label="Maximum Discount Cap (₹)" value={Number(theme.maxDiscount || 0)} min={0} step={0.01} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('maxDiscount', value > 0 ? value : null)} help="Optional. Enter the maximum discount any patient can receive; use 0 to remove the cap." />
        <NumberField label="Min Age" value={draft.minAge ?? 0} min={0} max={80} integer disabled={!canUpdate || busy !== ''} onChange={(value) => set('minAge', value || null)} />
        <NumberField label="Max Age" value={draft.maxAge ?? 0} min={0} max={80} integer disabled={!canUpdate || busy !== ''} onChange={(value) => set('maxAge', value || null)} />
        <CenterChecklistField
          label="Overall Eligible Centers"
          values={draft.eligibleCenters || []}
          options={campaignCenterOptions}
          disabled={!canUpdate || busy !== ''}
          onChange={(values) => set('eligibleCenters', values)}
        />
        <SelectField
          label="Overall Patient Eligibility"
          value={overallPatientMode}
          options={['ANY', 'NEW_REGISTRATION_RANGE', 'EXISTING_BEFORE', 'NEW_AND_EXISTING']}
          optionLabels={{
            ANY: 'Any patient',
            NEW_REGISTRATION_RANGE: 'New patient within registration range',
            EXISTING_BEFORE: 'Existing patient registered before date',
            NEW_AND_EXISTING: 'New and existing patients',
          }}
          disabled={!canUpdate || busy !== ''}
          onChange={(value) => setTheme('patientEligibilityMode', value)}
        />
        {overallPatientMode === 'NEW_REGISTRATION_RANGE' ? <>
          <TextField label="New Patient Registered From" type="date" value={String(theme.registrationDateFrom || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('registrationDateFrom', value)} help="Campaign-wide earliest eligible registration date." />
          <TextField label="New Patient Registered Through" type="date" value={String(theme.registrationDateTo || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('registrationDateTo', value)} help="Campaign-wide latest eligible registration date; inclusive." />
        </> : null}
        {overallPatientMode === 'EXISTING_BEFORE' ? (
          <TextField label="Registered Before" type="date" value={String(theme.existingRegisteredBefore || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('existingRegisteredBefore', value)} help="The patient registration date must be earlier than this date." />
        ) : null}
        <BookingWindowsField
          value={theme.bookingWindows}
          disabled={!canUpdate || busy !== ''}
          campaignStartsAt={draft.startsAt || null}
          campaignEndsAt={draft.endsAt || null}
          overallCenters={draft.eligibleCenters || []}
          availableCenters={campaignCenterOptions}
          overallPatientMode={overallPatientMode}
          overallRegistrationFrom={String(theme.registrationDateFrom || '')}
          overallRegistrationTo={String(theme.registrationDateTo || '')}
          overallExistingBefore={String(theme.existingRegisteredBefore || '')}
          overallAdvanceRule={normalizeObject(theme.requiredAdvanceAmountRule)}
          onValidityChange={(issue) => onInputIssue('bookingWindows', issue)}
          onChange={(value) => setTheme('bookingWindows', value)}
        />
        <SelectField
          label="Reservation Expiry Mode"
          value={theme.reservationExpiryMode === 'FIXED_CUTOFF' ? 'FIXED_CUTOFF' : 'RELATIVE_TTL'}
          options={['RELATIVE_TTL', 'FIXED_CUTOFF']}
          optionLabels={{ RELATIVE_TTL: 'Relative TTL from offer code', FIXED_CUTOFF: 'Fixed campaign cutoff date & time' }}
          disabled={!canUpdate || busy !== ''}
          onChange={(value) => setTheme('reservationExpiryMode', value)}
        />
        {theme.reservationExpiryMode === 'FIXED_CUTOFF' ? (
          <TextField
            label="Reservation Cutoff (IST)"
            type="datetime-local"
            value={toIstInput(typeof theme.reservationCutoffAt === 'string' ? theme.reservationCutoffAt : null)}
            disabled={!canUpdate || busy !== ''}
            onChange={(value) => setTheme('reservationCutoffAt', value ? istInputToIso(value) : '')}
          />
        ) : (
          <NumberField label="Reservation TTL Seconds" value={draft.reservationTtlSeconds} min={1} integer disabled={!canUpdate || busy !== ''} onChange={(value) => set('reservationTtlSeconds', value)} />
        )}
      </EditorSection>
    ),
    verification: (
      <EditorSection title="Advance Verification" help="Dynamic payment, package and plan validation against the advance Looker source">
        <SelectField label="Advance Amount Rule" value={String((theme.requiredAdvanceAmountRule as Record<string, unknown> | undefined)?.type || 'NONE')} disabled={!canUpdate || busy !== ''} options={['NONE', 'EXACT_AMOUNT', 'DISCOUNT_AMOUNT', 'MINIMUM_AMOUNT', 'RANGE']} onChange={(value) => setTheme('requiredAdvanceAmountRule', value === 'NONE' ? null : { type: value, amount: Number((theme.requiredAdvanceAmountRule as Record<string, unknown> | undefined)?.amount || 0) })} />
        {['EXACT_AMOUNT', 'MINIMUM_AMOUNT'].includes(String((theme.requiredAdvanceAmountRule as Record<string, unknown> | undefined)?.type || 'NONE')) ? <NumberField label="Required Advance Amount" value={Number((theme.requiredAdvanceAmountRule as Record<string, unknown> | undefined)?.amount || 0)} min={0} step={0.01} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('requiredAdvanceAmountRule', { ...(theme.requiredAdvanceAmountRule as Record<string, unknown> || {}), amount: value })} /> : null}
        {String((theme.requiredAdvanceAmountRule as Record<string, unknown> | undefined)?.type || '') === 'RANGE' ? <>
          <NumberField label="Minimum Advance Amount" value={Number((theme.requiredAdvanceAmountRule as Record<string, unknown> | undefined)?.minimumAmount || 0)} min={0} step={0.01} disabled={!canUpdate || busy !== ''} onChange={(minimumAmount) => setTheme('requiredAdvanceAmountRule', { ...(theme.requiredAdvanceAmountRule as Record<string, unknown> || {}), minimumAmount })} />
          <NumberField label="Maximum Advance Amount" value={Number((theme.requiredAdvanceAmountRule as Record<string, unknown> | undefined)?.maximumAmount || 0)} min={0} step={0.01} disabled={!canUpdate || busy !== ''} onChange={(maximumAmount) => setTheme('requiredAdvanceAmountRule', { ...(theme.requiredAdvanceAmountRule as Record<string, unknown> || {}), maximumAmount })} />
        </> : null}
        <NumberField label="Treatment Must Start Within Days" value={Number(theme.packageStartWithinDays ?? 0)} min={0} max={365} integer disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('packageStartWithinDays', value)} help="Booking day is day 0. A value of 30 permits treatment start through day 30." />
        <TextField label="Maximum Package End Date" type="date" value={String(theme.packageEndDateMax || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('packageEndDateMax', value)} />
        <JsonObjectField label="Allowed Package Plans JSON" value={{ plans: Array.isArray(theme.allowedPackagePlans) ? theme.allowedPackagePlans : [] }} disabled={!canUpdate || busy !== ''} help={'Use an object such as { "plans": ["IVF Advanced"] }.'} onValidityChange={(issue) => onInputIssue('allowedPackagePlans', issue)} onChange={(value) => setTheme('allowedPackagePlans', Array.isArray(value.plans) ? value.plans : [])} />
        <JsonObjectField label="Allowed Payment Statuses JSON" value={{ escrow: Array.isArray(theme.allowedEscrowStatuses) ? theme.allowedEscrowStatuses : [], item: Array.isArray(theme.allowedItemStatuses) ? theme.allowedItemStatuses : [] }} disabled={!canUpdate || busy !== ''} help={'Use an object with "escrow" and "item" arrays.'} onValidityChange={(issue) => onInputIssue('allowedPaymentStatuses', issue)} onChange={(value) => onChange({ ...draft, themeConfig: { ...theme, allowedEscrowStatuses: Array.isArray(value.escrow) ? value.escrow : [], allowedItemStatuses: Array.isArray(value.item) ? value.item : [] } })} />
        <ToggleField label="One MRN Once" value={theme.oneMrnOnce !== false} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('oneMrnOnce', value)} />
        <ToggleField label="Allow Admin Override" value={theme.allowAdminOverride === true} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('allowAdminOverride', value)} />
      </EditorSection>
    ),
    display: (
      <EditorSection title="Offer Code And Display" help="Dynamic code pattern and TV/display behavior">
        <TextField label="Code Pattern" value={draft.codePattern} disabled={!canUpdate || busy !== ''} onChange={(value) => setReviewed('codePattern', value.toUpperCase(), 'codePattern')} help="Review and save the generated offer-code format before validation." />
        <SelectField label="Sequence Scope" value={draft.codeSequenceScope} disabled={!canUpdate || busy !== ''} options={['GLOBAL', 'CENTER', 'DAY', 'CENTER_DAY']} onChange={(value) => set('codeSequenceScope', value)} />
        <NumberField label="Sequence Padding" value={Number(theme.sequencePadding ?? 3)} min={1} max={12} integer disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('sequencePadding', value)} />
        <NumberField label="Display Refresh Seconds" value={draft.displayRefreshSeconds} min={1} integer disabled={!canUpdate || busy !== ''} onChange={(value) => set('displayRefreshSeconds', value)} />
        <SelectField label="Celebration Effect" value={draft.celebrationEffect} disabled={!canUpdate || busy !== ''} options={celebrationOptions} onChange={(value) => set('celebrationEffect', value)} />
        <TextField label="Hero Title" value={String(theme.heroTitle || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('heroTitle', value)} />
        <TextField label="Subtitle" value={String(theme.subtitle || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('subtitle', value)} />
        <JsonObjectField label="Center Code Mapping JSON" value={normalizeObject(theme.centerCodes)} disabled={!canUpdate || busy !== ''} help={'Use an object such as { "Ferty9 Fertility Center - KPHB": "KPHB" }.'} onValidityChange={(issue) => onInputIssue('centerCodes', issue)} onChange={(value) => setTheme('centerCodes', value)} />
      </EditorSection>
    ),
    coupon: (
      <EditorSection title="Coupon And Terms" help="Dynamic public wording for the completed offer coupon. The official Ferty9 application logo is used automatically.">
        <TextField label="Coupon Title" value={String(theme.couponTitle || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('couponTitle', value)} />
        <TextField label="Coupon Subtitle" value={String(theme.couponSubtitle || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('couponSubtitle', value)} />
        <TextField label="Coupon Verification URL Template" value={String(theme.couponVerificationUrlTemplate || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('couponVerificationUrlTemplate', value)} help="Optional. Use a public HTTPS URL and include {couponPublicId}. Example: https://api.example.com/api/public/offer-coupons/{couponPublicId}" />
        {!couponValidityIsFixedDate && <NumberField label="Coupon Validity Duration" value={Number(theme.couponValidityDuration || 0)} min={0} integer disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('couponValidityDuration', value)} help="Set a positive whole number to calculate the exact coupon expiry from offer-code generation time. Use 0 to retain the existing package-date-only behaviour." />}
        <SelectField label="Coupon Validity Unit" value={String(theme.couponValidityUnit || 'DAYS')} disabled={!canUpdate || busy !== ''} onChange={setCouponValidityUnit} options={['DAYS', 'MONTHS', 'YEARS', 'FIXED_DATE']} optionLabels={{ DAYS: 'Days', MONTHS: 'Months', YEARS: 'Years', FIXED_DATE: 'Fixed Date' }} />
        {couponValidityIsFixedDate && <TextField label="Fixed Coupon Validity (IST)" type="datetime-local" value={toIstInput(typeof theme.couponValidityFixedAt === 'string' ? theme.couponValidityFixedAt : null)} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('couponValidityFixedAt', value ? istInputToIso(value) : '')} help="Every enrollment will receive this same coupon-expiry date and time." />}
        <TextAreaField label="Terms And Conditions" value={String(theme.couponTerms || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('couponTerms', value)} help="Enter one term per line." />
        <TextAreaField label="Patient Acknowledgement" value={String(theme.couponAcknowledgement || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('couponAcknowledgement', value)} />
        <TextAreaField label="Coupon Footer" value={String(theme.couponFooter || '')} disabled={!canUpdate || busy !== ''} onChange={(value) => setTheme('couponFooter', value)} />
      </EditorSection>
    ),
    analyticsEmail: (
      <>
      <AnalyticsEmailSettingsPanel
        mode={mode}
        campaignId={campaignId}
        enabled={theme.analyticsEmail as Record<string, unknown> | undefined}
        campaignName={draft.campaignName}
        campaignStartsAt={draft.startsAt}
        campaignEndsAt={draft.endsAt}
        disabled={!canUpdate || busy !== ''}
        actionsDisabled={hasUnsavedChanges || inputIssues.length > 0}
        preview={isAnalyticsEmailPreview(preview) ? null : preview}
        onClosePreview={onClosePreview}
        onChange={(value) => setTheme('analyticsEmail', value)}
        onPreview={onPreviewAnalyticsEmail}
        onSendTest={onSendAnalyticsEmailTest}
        onSendNow={onSendAnalyticsEmailNow}
      />
      {isAnalyticsEmailPreview(preview) ? <AnalyticsEmailPreviewModal html={preview.html} onClose={onClosePreview} /> : null}
      </>
    ),
    validate: (
      <EditorSection title="Validate And Activate" help="Validate dynamic configuration before business users can run the campaign">
        {saveIssues.length > 0 ? (
          <ValidationChecklist
            tone="error"
            title="Save not completed"
            help="Your entries remain in this editor. Review the message below and save again."
            issues={saveIssues}
          />
        ) : null}
        {hasUnsavedChanges && saveIssues.length === 0 ? (
          <ValidationChecklist
            tone="error"
            title="Unsaved changes"
            help="Save and verify the current form before running actions that use stored campaign data."
            issues={['Validate, Activate/Deactivate, source tests, code/display previews and email actions remain disabled until Save Workflow succeeds.']}
          />
        ) : null}
        {inputIssues.length > 0 ? (
          <ValidationChecklist
            tone="error"
            title="Invalid editor values"
            help="Correct these fields before saving."
            issues={inputIssues}
          />
        ) : null}
        <div className="offer-config-action-grid">
          <ActionButton label="Validate Workflow" loadingLabel="Validating..." loading={busy === 'Validate workflow'} disabled={storedActionDisabled} onClick={onValidate} />
          <ActionButton
            variant="primary"
            label={statusActionLabel}
            loadingLabel={statusActionLoadingLabel}
            loading={busy === statusActionBusyLabel}
            disabled={storedActionDisabled}
            onClick={workflowIsActive ? onDeactivate : onActivate}
          />
          <ActionButton label="Preview Code" loadingLabel="Loading Code..." loading={busy === 'Preview code'} disabled={storedActionDisabled} onClick={onPreviewCode} />
          <ActionButton label="Preview Display" loadingLabel="Loading Display..." loading={busy === 'Preview display'} disabled={storedActionDisabled} onClick={onPreviewDisplay} />
        </div>
        {validation ? (
          <ValidationChecklist
            tone={validation.ok ? 'success' : 'error'}
            title={validation.ok ? 'Validation passed' : 'Validation needs attention'}
          help={`Checked at ${formatOfferCampaignDateTime(validation.checkedAt)} IST`}
            issues={validation.ok ? ['Workflow configuration is valid.'] : validation.errors}
            warnings={validation.warnings}
          />
        ) : null}
        {preview ? (
          <div className="offer-preview-panel">
            <div className="offer-preview-title">Preview Output</div>
            <pre className="offer-admin-preview">{JSON.stringify(preview, null, 2)}</pre>
          </div>
        ) : null}
      </EditorSection>
    ),
  };

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="modal-overlay offer-admin-modal-overlay">
      <div className="modal-content admin-premium-modal general-settings-modal offer-admin-modal offer-workflow-editor-modal" role="dialog" aria-modal="true">
        <button type="button" onClick={onClose} aria-label="Close campaign workflow editor" className="admin-modal-close">&times;</button>
        <div className="offer-admin-modal-header">
          <p className="enterprise-eyebrow">Offer & Campaigns</p>
          <h3>{mode === 'add' ? 'Add Campaign Workflow' : 'Edit Campaign Workflow'}</h3>
          <p>This is the administration workflow. Business users only run active configured campaigns from Patient Engagement.</p>
        </div>
        <div className="workflow-editor-tabs offer-workflow-tabs">
          {campaignEditorPanels.map((panel, index) => (
            <button key={panel.key} type="button" className={activePanel === panel.key ? 'active' : ''} onClick={() => setActivePanel(panel.key)}>
              <span>{index + 1}</span>
              <strong>{panel.label}</strong>
              <small>{panel.help}</small>
            </button>
          ))}
        </div>
        <div className="offer-admin-modal-body">
          {sections[activePanel]}
        </div>
        <div className="offer-admin-modal-actions">
          <ActionButton label="Back" disabled={!canGoBack || busy !== ''} onClick={() => { goBack(); }} />
          <ActionButton label="Cancel" disabled={busy !== ''} onClick={onClose} />
          {canGoNext ? <ActionButton variant="primary" label="Next" disabled={busy !== ''} onClick={() => { goNext(); }} /> : null}
          <ActionButton
            variant="primary"
            label="Save Workflow"
            loadingLabel="Saving..."
            loading={busy === 'Save campaign workflow'}
            disabled={!canUpdate || busy !== ''}
            onClick={() => void onSave()}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function EditorSection({ title, help, children }: { title: string; help: string; children: ReactNode }) {
  return <section className="offer-config-section"><h4>{title}</h4><p>{help}</p><div className="offer-config-grid">{children}</div></section>;
}

type AnalyticsEmailConfig = {
  enabled: boolean;
  template: 'IVF_CAMPAIGN_OPTION_1_OUTLOOK_SAFE';
  frequency: 'DAILY';
  sendTimeIst: string;
  startMode: 'CAMPAIGN_START' | 'FIXED_DATE';
  startAt: string;
  endMode: 'CAMPAIGN_END' | 'FIXED_DATE' | 'UNTIL_DISABLED';
  endAt: string;
  finalReportEnabled: boolean;
  finalReportAt: string;
  subject: string;
  preheader: string;
  headerSlogan: string;
  campaignSubtitle: string;
  footerMessage: string;
  footerTagline: string;
  logoUrl: string;
  recipients: string[];
};

function normalizedAnalyticsEmailConfig(value: Record<string, unknown> | undefined, campaignName: string): AnalyticsEmailConfig {
  const input = value || {};
  return {
    enabled: input.enabled === true,
    template: 'IVF_CAMPAIGN_OPTION_1_OUTLOOK_SAFE',
    frequency: 'DAILY',
    sendTimeIst: typeof input.sendTimeIst === 'string' ? input.sendTimeIst : '09:00',
    startMode: input.startMode === 'FIXED_DATE' ? 'FIXED_DATE' : 'CAMPAIGN_START',
    startAt: typeof input.startAt === 'string' ? input.startAt : '',
    endMode: input.endMode === 'FIXED_DATE' || input.endMode === 'UNTIL_DISABLED' ? input.endMode : 'CAMPAIGN_END',
    endAt: typeof input.endAt === 'string' ? input.endAt : '',
    finalReportEnabled: input.finalReportEnabled === true,
    finalReportAt: typeof input.finalReportAt === 'string' ? input.finalReportAt : '',
    subject: typeof input.subject === 'string' ? input.subject : '{{campaign_name}} — {{report_date_time_ist}}',
    preheader: typeof input.preheader === 'string' ? input.preheader : 'Campaign performance summary across all eligible centers.',
    headerSlogan: typeof input.headerSlogan === 'string' ? input.headerSlogan : 'Empowering Dreams. Delivering Miracles.',
    campaignSubtitle: typeof input.campaignSubtitle === 'string' ? input.campaignSubtitle : '',
    footerMessage: typeof input.footerMessage === 'string' ? input.footerMessage : 'Together, creating more miracles every day.',
    footerTagline: typeof input.footerTagline === 'string' ? input.footerTagline : 'Ferty9 Fertility Center',
    logoUrl: typeof input.logoUrl === 'string' ? input.logoUrl : '',
    recipients: Array.isArray(input.recipients) ? input.recipients.filter((item): item is string => typeof item === 'string') : [],
  };
}

function AnalyticsEmailSettingsPanel({
  mode,
  campaignId,
  enabled,
  campaignName,
  campaignStartsAt,
  campaignEndsAt,
  disabled,
  actionsDisabled,
  preview,
  onClosePreview,
  onChange,
  onPreview,
  onSendTest,
  onSendNow,
}: {
  mode: 'add' | 'edit';
  campaignId: string;
  enabled?: Record<string, unknown>;
  campaignName: string;
  campaignStartsAt?: string | null;
  campaignEndsAt?: string | null;
  disabled: boolean;
  actionsDisabled: boolean;
  preview: unknown;
  onClosePreview: () => void;
  onChange: (value: AnalyticsEmailConfig) => void;
  onPreview: () => void | Promise<void>;
  onSendTest: (recipient: string) => void | Promise<void>;
  onSendNow: () => void | Promise<void>;
}) {
  const [recipient, setRecipient] = useState('');
  const [recipientError, setRecipientError] = useState('');
  const [testRecipient, setTestRecipient] = useState('');
  const [deliveryHistory, setDeliveryHistory] = useState<OfferCampaignAnalyticsEmailDelivery[]>([]);
  const [deliveryHistoryError, setDeliveryHistoryError] = useState('');
  const [deliveryHistoryLoading, setDeliveryHistoryLoading] = useState(false);
  const config = normalizedAnalyticsEmailConfig(enabled, campaignName);
  const set = <K extends keyof AnalyticsEmailConfig>(key: K, value: AnalyticsEmailConfig[K]) => onChange({ ...config, [key]: value });
  const addRecipient = () => {
    const email = recipient.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setRecipientError('Enter a valid recipient email address.');
      return;
    }
    if (config.recipients.some((item) => item.toLowerCase() === email)) {
      setRecipientError('This recipient is already in the list.');
      return;
    }
    set('recipients', [...config.recipients, email]);
    setRecipient('');
    setRecipientError('');
  };
  const loadDeliveryHistory = async () => {
    if (!campaignId) return;
    setDeliveryHistoryLoading(true);
    setDeliveryHistoryError('');
    try {
      const result = await listOfferCampaignAnalyticsEmailDeliveries(campaignId);
      setDeliveryHistory(result.deliveries);
    } catch (error) {
      setDeliveryHistoryError(error instanceof Error ? error.message : 'Unable to load delivery history.');
    } finally {
      setDeliveryHistoryLoading(false);
    }
  };

  if (mode === 'add') {
    return <EditorSection title="Analytics Email" help="Save the campaign workflow first. You can then configure its scheduled analytics email without affecting enrollment or display behaviour."><div className="offer-config-source-box"><strong>Campaign save required</strong><small>Create this workflow first, then reopen it and configure recipients, timing and email wording here.</small></div></EditorSection>;
  }

  return (
    <EditorSection title="Analytics Email" help="Configure the Outlook-safe IVF campaign analytics email. This only saves configuration; automated sending is enabled in the delivery step.">
      <div className="offer-analytics-email-layout">
        <section className="offer-analytics-email-card">
          <div className="offer-analytics-email-card-heading">
            <div><strong>Delivery Settings</strong><small>Enable the report and choose its daily IST send time.</small></div>
            <ToggleField label="Enable Analytics Email" value={config.enabled} disabled={disabled} onChange={(value) => set('enabled', value)} />
          </div>
          {config.enabled ? <div className="offer-analytics-email-fields offer-analytics-email-fields-three">
      <SelectField label="Email Template" value={config.template} disabled options={['IVF_CAMPAIGN_OPTION_1_OUTLOOK_SAFE']} optionLabels={{ IVF_CAMPAIGN_OPTION_1_OUTLOOK_SAFE: 'IVF Campaign Analytics — Outlook Safe' }} onChange={() => undefined} />
      <TextField label="Timezone" value="Asia/Kolkata (IST)" disabled onChange={() => undefined} />
      <TextField label="Send Daily At (IST)" type="time" value={config.sendTimeIst} disabled={disabled} onChange={(value) => set('sendTimeIst', value)} />
          </div> : <p className="offer-analytics-email-disabled-note">Analytics Email is disabled. Saving this workflow cancels any report still waiting to send. Enrollment, coupons, and display boards are unchanged.</p>}
          {config.enabled ? <p className="offer-analytics-email-disabled-note">The saved schedule is checked every minute. A brief worker delay is caught up safely on the same IST day, and a report already sent for that scheduled time is not sent twice.</p> : null}
        </section>
        {config.enabled ? <>
        <section className="offer-analytics-email-card">
          <div className="offer-analytics-email-card-heading"><div><strong>Schedule Window</strong><small>Reports run only while the campaign is active and inside this IST window.</small></div></div>
          <div className="offer-analytics-email-fields">
      <SelectField label="Start Sending" value={config.startMode} disabled={disabled} options={['CAMPAIGN_START', 'FIXED_DATE']} optionLabels={{ CAMPAIGN_START: `Campaign start (${formatIstDateTime(campaignStartsAt)})`, FIXED_DATE: 'Fixed date and time' }} onChange={(value) => set('startMode', value as AnalyticsEmailConfig['startMode'])} />
      {config.startMode === 'FIXED_DATE' && <TextField label="Fixed Start Date/Time (IST)" type="datetime-local" value={toIstInput(config.startAt || null)} disabled={disabled} onChange={(value) => set('startAt', value ? istInputToIso(value) : '')} />}
      <SelectField label="Stop Sending" value={config.endMode} disabled={disabled} options={['CAMPAIGN_END', 'FIXED_DATE', 'UNTIL_DISABLED']} optionLabels={{ CAMPAIGN_END: `Campaign end (${formatIstDateTime(campaignEndsAt)})`, FIXED_DATE: 'Fixed date and time', UNTIL_DISABLED: 'Continue until disabled' }} onChange={(value) => set('endMode', value as AnalyticsEmailConfig['endMode'])} />
      {config.endMode === 'FIXED_DATE' && <TextField label="Fixed End Date/Time (IST)" type="datetime-local" value={toIstInput(config.endAt || null)} disabled={disabled} onChange={(value) => set('endAt', value ? istInputToIso(value) : '')} />}
      <ToggleField label="Send Final Report" value={config.finalReportEnabled} disabled={disabled} onChange={(value) => set('finalReportEnabled', value)} />
      {config.finalReportEnabled && <TextField label="Final Report Date/Time (IST)" type="datetime-local" value={toIstInput(config.finalReportAt || null)} disabled={disabled} onChange={(value) => set('finalReportAt', value ? istInputToIso(value) : '')} help="Sends one final report at this time, replaces that date’s daily report, then disables this email schedule." />}
          </div>
        </section>

        <section className="offer-analytics-email-card">
          <div className="offer-analytics-email-card-heading"><div><strong>Report Recipients</strong><small>Each saved recipient receives an individual daily report.</small></div><span className="offer-analytics-email-count">{config.recipients.length} saved</span></div>
          <div className="offer-config-source-box offer-analytics-email-source-box">
        <div className="offer-config-action-grid">
          <input aria-label="Recipient email address" type="email" value={recipient} disabled={disabled} placeholder="name@ferty9.com" onChange={(event) => { setRecipient(event.target.value); setRecipientError(''); }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addRecipient(); } }} />
          <button type="button" className="mini-btn pink" disabled={disabled || !recipient.trim()} onClick={addRecipient}>Add Recipient</button>
        </div>
        {recipientError ? <small className="offer-config-error">{recipientError}</small> : null}
        {config.recipients.length === 0 ? <small>No recipients added yet.</small> : <div className="offer-email-recipient-list">{config.recipients.map((email) => <span key={email}>{email}<button type="button" aria-label={`Remove ${email}`} disabled={disabled} onClick={() => set('recipients', config.recipients.filter((item) => item !== email))}>&times;</button></span>)}</div>}
          </div>
        </section>

        <section className="offer-analytics-email-card">
          <div className="offer-analytics-email-card-heading"><div><strong>Email Content</strong><small>Personalize the Outlook-safe report template. Live campaign metrics are inserted during delivery.</small></div></div>
          <div className="offer-analytics-email-fields">
      <TextField label="Email Subject" value={config.subject} disabled={disabled} onChange={(value) => set('subject', value)} help="Supported: {{campaign_name}}, {{report_date_time_ist}}, {{report_date_ist}}, {{report_time_ist}}, {{report_type}}. Example: {{campaign_name}} — {{report_date_time_ist}}" />
      <TextField label="Preheader" value={config.preheader} disabled={disabled} onChange={(value) => set('preheader', value)} />
      <TextField label="Header Slogan" value={config.headerSlogan} disabled={disabled} onChange={(value) => set('headerSlogan', value)} />
      <TextField label="Campaign Subtitle" value={config.campaignSubtitle} disabled={disabled} onChange={(value) => set('campaignSubtitle', value)} />
      <TextField label="Footer Message" value={config.footerMessage} disabled={disabled} onChange={(value) => set('footerMessage', value)} />
      <TextField label="Footer Tagline" value={config.footerTagline} disabled={disabled} onChange={(value) => set('footerTagline', value)} />
      <TextField label="Logo URL (optional)" value={config.logoUrl} disabled={disabled} onChange={(value) => set('logoUrl', value)} />
          </div>
          <details className="offer-analytics-email-mapping">
            <summary>Template Data Mapping</summary>
            <p>Read-only live values inserted at preview/send time. No patient, MRN, payment, or Looker-row data is included.</p>
            <table><thead><tr><th>Template value</th><th>Live source</th></tr></thead><tbody>
              <tr><td>Report date and time</td><td>Current Asia/Kolkata reporting timestamp</td></tr>
              <tr><td>Subject variables</td><td>{'{{campaign_name}}, {{report_date_time_ist}}, {{report_date_ist}}, {{report_time_ist}}, {{report_type}}'}</td></tr>
              <tr><td>Total / B2B / B2C</td><td>Completed enrollments for this campaign</td></tr>
              <tr><td>Center leaderboard and today values</td><td>Completed enrollment center totals in IST</td></tr>
              <tr><td>Top doctors and employees</td><td>Completed ranking data; employee display name falls back to username</td></tr>
              <tr><td>Campaign title and wording</td><td>Saved campaign and Email Content settings</td></tr>
            </tbody></table>
          </details>
        </section>
        <section className="offer-analytics-email-card offer-analytics-email-actions-card">
          <div className="offer-analytics-email-card-heading"><div><strong>Manual Actions</strong><small>Preview does not send. Send Now uses saved recipients. Test sends only to the temporary address.</small></div></div>
          {actionsDisabled ? <small className="offer-config-error">Save Workflow first so these actions use the verified database configuration.</small> : null}
          <div className="offer-config-source-box offer-analytics-email-source-box">
        <div className="offer-config-action-grid">
          <ActionButton className="mini-btn" label="Preview Email" loadingLabel="Loading..." disabled={disabled || actionsDisabled} onClick={onPreview} />
          <ActionButton className="mini-btn pink" label="Send Now" loadingLabel="Sending..." disabled={disabled || actionsDisabled || !config.recipients.length} onClick={onSendNow} />
        </div>
        <div className="offer-config-action-grid">
          <input aria-label="Temporary test recipient" type="email" value={testRecipient} disabled={disabled} placeholder="test-recipient@ferty9.com" onChange={(event) => setTestRecipient(event.target.value)} />
          <ActionButton className="mini-btn" label="Send Test Email" loadingLabel="Sending..." disabled={disabled || actionsDisabled || !testRecipient.trim()} onClick={() => onSendTest(testRecipient)} />
        </div>
          </div>
        </section>
        <section className="offer-analytics-email-card offer-analytics-email-history-card">
          <div className="offer-analytics-email-card-heading"><div><strong>Delivery History</strong><small>Recent scheduled, manual, and test report outcomes. Recipient addresses and patient data are never shown here.</small></div><ActionButton className="mini-btn" label="Refresh History" loadingLabel="Loading..." loading={deliveryHistoryLoading} disabled={disabled} onClick={loadDeliveryHistory} /></div>
          {deliveryHistoryError ? <small className="offer-config-error">{deliveryHistoryError}</small> : null}
          {deliveryHistory.length === 0 ? <p className="offer-analytics-email-empty">Select Refresh History to load the most recent 50 delivery records.</p> : <div className="offer-analytics-email-history-table"><table><thead><tr><th>When</th><th>Type</th><th>Snapshot</th><th>Provider</th><th>Status</th></tr></thead><tbody>{deliveryHistory.map((delivery) => <tr key={delivery.id}><td>{formatOfferCampaignDateTime(delivery.sentAt || delivery.scheduledForIst || delivery.createdAt)} IST</td><td>{analyticsDeliveryTypeLabel(delivery.triggerType)}</td><td>{delivery.snapshotTotal} total · {delivery.snapshotB2b} B2B · {delivery.snapshotB2c} B2C</td><td>{delivery.provider || '—'}</td><td><span className={`status-chip ${delivery.status === 'SENT' ? 'status-good' : delivery.status === 'FAILED' ? 'status-danger' : 'status-muted'}`}>{analyticsDeliveryStatusLabel(delivery)}</span></td></tr>)}</tbody></table></div>}
        </section>
        </> : null}
      {isAnalyticsEmailPreview(preview) ? <div className="offer-email-preview"><strong>Controlled Email Preview — no email sent</strong><iframe title="Campaign analytics email preview" sandbox="" srcDoc={preview.html} /></div> : null}
      {isAnalyticsEmailActionResult(preview) ? <div className="offer-config-source-box"><strong>Email action result</strong><small>Provider: {preview.provider || 'not sent'} · {preview.success ? 'Sent successfully' : preview.skipped ? 'Skipped' : 'Not sent'}{preview.sentAt ? ` · ${formatOfferCampaignDateTime(preview.sentAt)} IST` : ''}{preview.reason ? ` · ${preview.reason}` : ''}</small></div> : null}
      </div>
    </EditorSection>
  );
}

function isAnalyticsEmailPreview(value: unknown): value is { html: string; subject: string } {
  return Boolean(value && typeof value === 'object' && typeof (value as { html?: unknown }).html === 'string');
}

function analyticsDeliveryTypeLabel(trigger: OfferCampaignAnalyticsEmailDelivery['triggerType']): string {
  return ({ SCHEDULED: 'Daily schedule', FINAL: 'Final report', MANUAL: 'Sent now', TEST: 'Test email' })[trigger];
}

function analyticsDeliveryStatusLabel(delivery: OfferCampaignAnalyticsEmailDelivery): string {
  if (delivery.status === 'SENT') return 'Sent';
  if (delivery.status === 'SENDING') return 'Sending';
  if (delivery.status === 'QUEUED') return 'Waiting to send';
  if (delivery.status === 'CANCELLED' || delivery.errorCategory === 'SCHEDULE_DISABLED') return 'Cancelled when schedule was disabled';
  if (delivery.errorCategory === 'MISSED_RUN') return 'Scheduled report was missed';
  if (delivery.errorCategory === 'PROVIDER_TIMEOUT') return 'Email provider timed out; delivery will retry';
  return 'Delivery failed; delivery will retry';
}

function AnalyticsEmailPreviewModal({ html, onClose }: { html: string; onClose: () => void }) {
  return (
    <div className="offer-email-preview-modal-backdrop" role="presentation">
      <section className="offer-email-preview-modal" role="dialog" aria-modal="true" aria-label="Campaign analytics email preview">
        <header><div><strong>Controlled Email Preview</strong><small>No email has been sent.</small></div><button type="button" className="mini-btn" onClick={onClose}>Close</button></header>
        <iframe title="Campaign analytics email preview" sandbox="" srcDoc={html} />
      </section>
    </div>
  );
}

function isAnalyticsEmailActionResult(value: unknown): value is { success?: boolean; skipped?: boolean; provider?: string; sentAt?: string; reason?: string } {
  return Boolean(value && typeof value === 'object' && !('html' in value) && ('success' in value || 'skipped' in value));
}

function ActionButton({
  label,
  loadingLabel,
  loading = false,
  disabled = false,
  onClick,
  variant = 'outline',
  className = '',
}: {
  label: string;
  loadingLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void | Promise<void>;
  variant?: 'outline' | 'primary';
  className?: string;
}) {
  const baseClass = className || `btn ${variant === 'primary' ? 'btn-primary' : 'btn-outline'}`;
  return (
    <button
      type="button"
      className={`${baseClass} offer-action-button${loading ? ' is-loading' : ''}`}
      disabled={disabled || loading}
      onClick={() => void onClick()}
    >
      {loading ? <span className="offer-action-spinner" aria-hidden="true" /> : null}
      <span>{loading ? loadingLabel || label : label}</span>
    </button>
  );
}

function ValidationChecklist({
  tone,
  title,
  help,
  issues,
  warnings = [],
}: {
  tone: 'error' | 'success';
  title: string;
  help: string;
  issues: string[];
  warnings?: string[];
}) {
  return (
    <div className={`offer-validation-checklist ${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <div>
        <strong>{title}</strong>
        <span>{help}</span>
      </div>
      {issues.length > 0 ? (
        <ul>
          {issues.map((issue, index) => (
            <li key={`${tone}-issue-${index}`}>{issue}</li>
          ))}
        </ul>
      ) : null}
      {warnings.length > 0 ? (
        <div className="offer-validation-warnings">
          <strong>Warnings</strong>
          <ul>
            {warnings.map((warning, index) => (
              <li key={`warning-${index}`}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function MappingEditor({ title, type, keys, draft, tests, disabled, onChange }: { title: string; type: 'PATIENT_REGISTRATION' | 'ADVANCE_PAYMENT'; keys: MappingField[]; draft: OfferCampaignAdminPayload; tests: Record<string, OfferCampaignLookerTestResult>; disabled: boolean; onChange: (draft: OfferCampaignAdminPayload) => void }) {
  const source = sourceFor(draft, type);
  const previewFields = tests[type]?.fields.map((field) => field.fieldKey) || [];
  const savedFields = keys
    .map(({ key }) => source.fieldMappings[key])
    .filter((field): field is string => typeof field === 'string' && field.trim().length > 0);
  const fields = [...new Set([...previewFields, ...savedFields])];
  return (
    <div className="offer-config-source-box">
      <strong>{title}</strong>
      {keys.map(({ key, label }) => (
        <SelectField key={key} label={label} value={String(source.fieldMappings[key] || '')} disabled={disabled} options={['', ...fields]} onChange={(value) => updateSource(draft, onChange, type, { fieldMappings: { ...source.fieldMappings, [key]: value } })} />
      ))}
    </div>
  );
}

function TextField({ label, value, onChange, type = 'text', help, disabled = false }: { label: string; value: string; onChange: (value: string) => void; type?: string; help?: string; disabled?: boolean }) {
  const dateKind = type === 'date' || type === 'datetime-local' ? type : null;
  return <label className="offer-config-field"><span>{label}</span>{dateKind
    ? <OfferCampaignDateInput kind={dateKind} value={value} disabled={disabled} onChange={onChange} />
    : <input type={type} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />}{dateKind && <small>Format: {dateKind === 'date' ? 'DD/MM/YYYY' : 'DD/MM/YYYY hh:mm AM/PM'}</small>}{help && <small>{help}</small>}</label>;
}

function TextAreaField({ label, value, onChange, help, disabled = false }: { label: string; value: string; onChange: (value: string) => void; help?: string; disabled?: boolean }) {
  return <label className="offer-config-field offer-config-field-wide"><span>{label}</span><textarea rows={5} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />{help && <small>{help}</small>}</label>;
}

function NumberField({ label, value, min, max, step, integer = false, disabled = false, onChange, help }: { label: string; value: number; min?: number; max?: number; step?: number; integer?: boolean; disabled?: boolean; onChange: (value: number) => void; help?: string }) {
  const update = (rawValue: string) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;
    const wholeValue = integer ? Math.trunc(parsed) : parsed;
    onChange(Math.min(max ?? wholeValue, Math.max(min ?? wholeValue, wholeValue)));
  };
  return <label className="offer-config-field"><span>{label}</span><input type="number" value={Number.isFinite(value) ? value : 0} min={min} max={max} step={integer ? 1 : step} disabled={disabled} onChange={(event) => update(event.target.value)} />{help && <small>{help}</small>}</label>;
}

function SelectField({ label, value, options, optionLabels, disabledOptions = [], disabled = false, onChange }: { label: string; value: string; options: string[]; optionLabels?: Record<string, string>; disabledOptions?: string[]; disabled?: boolean; onChange: (value: string) => void }) {
  return <label className="offer-config-field"><span>{label}</span><select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option} value={option} disabled={disabledOptions.includes(option)}>{optionLabels?.[option] || option || 'Select'}</option>)}</select></label>;
}

function JsonObjectField({
  label,
  value,
  disabled = false,
  help,
  onValidityChange,
  onChange,
}: {
  label: string;
  value: Record<string, unknown>;
  disabled?: boolean;
  help: string;
  onValidityChange: (issue: string) => void;
  onChange: (value: Record<string, unknown>) => void;
}) {
  const serialized = JSON.stringify(value || {}, null, 2);
  const [text, setText] = useState(serialized);
  const [error, setError] = useState('');

  useEffect(() => {
    setText(serialized);
    setError('');
    onValidityChange('');
  }, [serialized]);

  const updateText = (next: string) => {
    setText(next);
    try {
      const parsed = parseJsonObject(next);
      setError('');
      onValidityChange('');
      onChange(parsed);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Enter a valid JSON object.';
      setError(message);
      onValidityChange(`${label}: ${message}`);
    }
  };

  return (
    <label className="offer-config-field offer-config-field-wide offer-config-json-field">
      <span>{label}</span>
      <textarea
        rows={Math.min(12, Math.max(5, text.split('\n').length))}
        value={text}
        disabled={disabled}
        spellCheck={false}
        onChange={(event) => updateText(event.target.value)}
      />
      <small className={error ? 'offer-config-error' : 'offer-config-help'}>
        {error || help}
      </small>
    </label>
  );
}

function ToggleField({ label, value, disabled = false, onChange }: { label: string; value: boolean; disabled?: boolean; onChange: (value: boolean) => void }) {
  return <label className="offer-config-toggle"><input type="checkbox" checked={value} disabled={disabled} onChange={(event) => onChange(event.target.checked)} /><span>{label}</span></label>;
}

function BookingWindowsField({
  value,
  disabled = false,
  campaignStartsAt,
  campaignEndsAt,
  overallCenters,
  availableCenters,
  overallPatientMode,
  overallRegistrationFrom,
  overallRegistrationTo,
  overallExistingBefore,
  overallAdvanceRule,
  onValidityChange,
  onChange,
}: {
  value: unknown;
  disabled?: boolean;
  campaignStartsAt: string | null;
  campaignEndsAt: string | null;
  overallCenters: string[];
  availableCenters: string[];
  overallPatientMode: string;
  overallRegistrationFrom: string;
  overallRegistrationTo: string;
  overallExistingBefore: string;
  overallAdvanceRule: Record<string, unknown>;
  onValidityChange?: (issue: string) => void;
  onChange: (value: Array<Record<string, unknown>>) => void;
}) {
  const windows = Array.isArray(value)
    ? value.map((item, index) => normalizeBookingWindow(item, index))
    : [];
  const permittedWindowCenters = overallCenters.length > 0 ? overallCenters : availableCenters;
  const issuesByWindow = bookingWindowClientIssues(windows, {
    campaignStartsAt,
    campaignEndsAt,
    overallCenters,
    overallPatientMode,
    overallRegistrationFrom,
    overallRegistrationTo,
    overallExistingBefore,
  });
  const combinedIssue = issuesByWindow.flat().join('; ');
  const lastReportedIssue = useRef<string | null>(null);
  useEffect(() => {
    if (lastReportedIssue.current === combinedIssue) return;
    lastReportedIssue.current = combinedIssue;
    onValidityChange?.(combinedIssue);
  }, [combinedIssue, onValidityChange]);
  const updateWindow = (index: number, patch: Record<string, unknown>) => {
    const next = windows.map((window, windowIndex) => windowIndex === index
      ? { ...window, ...patch }
      : window);
    onChange(next);
  };
  const campaignBoundaryHelp = campaignStartsAt && campaignEndsAt
    ? `Every window must stay between ${formatWindowDate(campaignStartsAt)} and ${formatWindowDate(campaignEndsAt)}.`
    : 'Set the campaign start and end date/time in Step 1 before saving booking windows.';
  return (
    <div className="offer-config-source-box offer-config-field-wide">
      <strong>Booking Windows</strong>
      <small>{campaignBoundaryHelp} Windows cannot overlap, and window rules may only narrow the overall rules above.</small>
      {windows.map((window, index) => (
        <details key={`${window.key}-${index}`} className="offer-booking-window-card" open>
          <summary>
            <strong>{window.key || `WINDOW_${index + 1}`}</strong>
            <span>{windowSummary(window, overallAdvanceRule)}</span>
          </summary>
          <div className="offer-config-grid offer-booking-window-grid">
            <TextField label="Window Key" value={window.key} disabled={disabled} onChange={(next) => updateWindow(index, { key: next.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} help="Unique stable key stored with each reservation." />
            <TextField label={`Window ${index + 1} Starts (IST)`} type="datetime-local" value={toIstInput(window.startsAt)} disabled={disabled} onChange={(next) => updateWindow(index, { startsAt: next ? istInputToIso(next) : '' })} />
            <TextField label={`Window ${index + 1} Ends (IST)`} type="datetime-local" value={toIstInput(window.endsAt)} disabled={disabled} onChange={(next) => updateWindow(index, { endsAt: next ? istInputToIso(next) : '' })} />
            <SelectField
              label="Centre Rule"
              value={String(window.centerRule.mode || 'INHERIT')}
              options={['INHERIT', 'SUBSET']}
              optionLabels={{ INHERIT: 'Inherit overall centres', SUBSET: 'Select a subset' }}
              disabled={disabled}
              onChange={(mode) => updateWindow(index, { centerRule: { ...window.centerRule, mode } })}
            />
            {window.centerRule.mode === 'SUBSET' ? (
              <CenterChecklistField
                label="Centres Eligible In This Window"
                values={Array.isArray(window.centerRule.eligibleCenters) ? window.centerRule.eligibleCenters.map(String) : []}
                options={permittedWindowCenters}
                disabled={disabled}
                onChange={(eligibleCenters) => updateWindow(index, { centerRule: { mode: 'SUBSET', eligibleCenters } })}
              />
            ) : null}
            <SelectField
              label="Patient Rule"
              value={String(window.patientRule.mode || 'INHERIT')}
              options={['INHERIT', 'NEW_IN_WINDOW', 'NEW_REGISTRATION_RANGE', 'EXISTING', 'ANY_PERMITTED_OVERALL']}
              optionLabels={{
                INHERIT: 'Inherit overall patient rule',
                NEW_IN_WINDOW: 'New patient in this booking window',
                NEW_REGISTRATION_RANGE: 'Custom new-patient registration range',
                EXISTING: 'Existing patient',
                ANY_PERMITTED_OVERALL: 'Any patient permitted overall',
              }}
              disabled={disabled}
              onChange={(mode) => updateWindow(index, { patientRule: { ...window.patientRule, mode } })}
            />
            {window.patientRule.mode === 'NEW_IN_WINDOW' ? (
              <div className="offer-config-source-box offer-effective-rule"><strong>Effective registration range</strong><small>{formatWindowDate(window.startsAt)} through {formatWindowDate(window.endsAt)}, restricted by overall {overallPatientMode}{overallRegistrationFrom || overallRegistrationTo ? ` (${overallRegistrationFrom ? formatOfferCampaignDate(overallRegistrationFrom) : 'open'} to ${overallRegistrationTo ? formatOfferCampaignDate(overallRegistrationTo) : 'open'})` : ''}.</small></div>
            ) : null}
            {['CUSTOM_RANGE', 'NEW_REGISTRATION_RANGE'].includes(String(window.patientRule.mode)) ? <>
              <TextField label="Registration From" type="date" value={String(window.patientRule.registrationFrom || '')} disabled={disabled} onChange={(registrationFrom) => updateWindow(index, { patientRule: { ...window.patientRule, registrationFrom } })} />
              <TextField label="Registration Through" type="date" value={String(window.patientRule.registrationTo || '')} disabled={disabled} onChange={(registrationTo) => updateWindow(index, { patientRule: { ...window.patientRule, registrationTo } })} />
            </> : null}
            {window.patientRule.mode === 'EXISTING' ? (
              <TextField label="Registered Before" type="date" value={String(window.patientRule.existingRegisteredBefore || '')} disabled={disabled} onChange={(existingRegisteredBefore) => updateWindow(index, { patientRule: { ...window.patientRule, existingRegisteredBefore } })} />
            ) : null}
            <SelectField
              label="Advance Rule"
              value={String(window.advanceRule.mode || 'INHERIT')}
              options={['INHERIT', 'EXACT_AMOUNT', 'MINIMUM_AMOUNT', 'RANGE', 'DISCOUNT_AMOUNT']}
              optionLabels={{
                INHERIT: 'Inherit overall advance rule',
                EXACT_AMOUNT: 'Exact amount',
                MINIMUM_AMOUNT: 'Minimum amount',
                RANGE: 'Variable amount range',
                DISCOUNT_AMOUNT: 'Equal to calculated discount',
              }}
              disabled={disabled}
              onChange={(mode) => updateWindow(index, { advanceRule: { ...window.advanceRule, mode } })}
            />
            {['EXACT_AMOUNT', 'MINIMUM_AMOUNT'].includes(String(window.advanceRule.mode)) ? (
              <NumberField label="Advance Amount (₹)" value={Number(window.advanceRule.amount || 0)} min={0} step={0.01} disabled={disabled} onChange={(amount) => updateWindow(index, { advanceRule: { ...window.advanceRule, amount } })} />
            ) : null}
            {window.advanceRule.mode === 'RANGE' ? <>
              <NumberField label="Minimum Advance (₹)" value={Number(window.advanceRule.minimumAmount || 0)} min={0} step={0.01} disabled={disabled} onChange={(minimumAmount) => updateWindow(index, { advanceRule: { ...window.advanceRule, minimumAmount } })} />
              <NumberField label="Maximum Advance (₹)" value={Number(window.advanceRule.maximumAmount || 0)} min={0} step={0.01} disabled={disabled} onChange={(maximumAmount) => updateWindow(index, { advanceRule: { ...window.advanceRule, maximumAmount } })} />
            </> : null}
            <div className="offer-config-source-box offer-effective-rule"><strong>Effective rule summary</strong><small>{windowEffectiveSummary(window, overallAdvanceRule)}</small></div>
            {issuesByWindow[index].length > 0 ? (
              <div className="offer-window-validation" role="alert">
                <strong>Fix this booking window</strong>
                <ul>{issuesByWindow[index].map((issue) => <li key={issue}>{issue}</li>)}</ul>
              </div>
            ) : null}
            <button type="button" className="mini-btn offer-remove-window" disabled={disabled} onClick={() => onChange(windows.filter((_, windowIndex) => windowIndex !== index))}>Remove Window</button>
          </div>
        </details>
      ))}
      <button type="button" className="mini-btn pink" disabled={disabled} onClick={() => onChange([...windows, normalizeBookingWindow({}, windows.length)])}>Add Booking Window</button>
    </div>
  );
}

type BookingWindowDraft = {
  key: string;
  startsAt: string;
  endsAt: string;
  centerRule: Record<string, unknown>;
  patientRule: Record<string, unknown>;
  advanceRule: Record<string, unknown>;
};

function normalizeBookingWindow(value: unknown, index: number): BookingWindowDraft {
  const record = normalizeObject(value);
  const patientRule = normalizeObject(record.patientRule);
  const patientRuleMode = String(patientRule.mode || 'INHERIT').toUpperCase();
  return {
    key: String(record.key || `WINDOW_${index + 1}`).trim().toUpperCase(),
    startsAt: String(record.startsAt || ''),
    endsAt: String(record.endsAt || ''),
    centerRule: { mode: 'INHERIT', ...normalizeObject(record.centerRule) },
    patientRule: {
      ...patientRule,
      mode: patientRuleMode === 'CUSTOM_RANGE' ? 'NEW_REGISTRATION_RANGE' : patientRuleMode,
    },
    advanceRule: { mode: 'INHERIT', ...normalizeObject(record.advanceRule) },
  };
}

function bookingWindowClientIssues(
  windows: BookingWindowDraft[],
  context: {
    campaignStartsAt: string | null;
    campaignEndsAt: string | null;
    overallCenters: string[];
    overallPatientMode: string;
    overallRegistrationFrom: string;
    overallRegistrationTo: string;
    overallExistingBefore: string;
  },
): string[][] {
  const issues = windows.map(() => [] as string[]);
  const parsed = windows.map((window) => ({
    startsAt: Date.parse(window.startsAt),
    endsAt: Date.parse(window.endsAt),
  }));
  const campaignStart = context.campaignStartsAt ? Date.parse(context.campaignStartsAt) : Number.NaN;
  const campaignEnd = context.campaignEndsAt ? Date.parse(context.campaignEndsAt) : Number.NaN;
  const normalizedOverallCenters = new Set(context.overallCenters.map(normalizedCenterToken));
  const keyCounts = new Map<string, number>();
  windows.forEach((window) => keyCounts.set(window.key, (keyCounts.get(window.key) || 0) + 1));

  windows.forEach((window, index) => {
    const start = parsed[index].startsAt;
    const end = parsed[index].endsAt;
    if (!/^[A-Z0-9_]{1,50}$/.test(window.key)) issues[index].push('Window key must use only A-Z, 0-9 and underscore.');
    if ((keyCounts.get(window.key) || 0) > 1) issues[index].push(`Window key ${window.key || '(blank)'} is duplicated.`);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      issues[index].push('Enter a valid end date/time after the start date/time.');
    } else if (!Number.isFinite(campaignStart) || !Number.isFinite(campaignEnd)) {
      issues[index].push('Set the campaign start and end date/time in Step 1.');
    } else if (start < campaignStart || end > campaignEnd) {
      issues[index].push('The window must remain inside the campaign start and end date/time.');
    }

    const centerMode = String(window.centerRule.mode || 'INHERIT').toUpperCase();
    if (!['INHERIT', 'SUBSET'].includes(centerMode)) issues[index].push('Select a valid centre rule.');
    if (centerMode === 'SUBSET') {
      const selected = Array.isArray(window.centerRule.eligibleCenters)
        ? window.centerRule.eligibleCenters.map(String).filter(Boolean)
        : [];
      if (selected.length === 0) issues[index].push('Select at least one eligible centre for this window.');
      if (normalizedOverallCenters.size > 0) {
        const outside = selected.filter((center) => !normalizedOverallCenters.has(normalizedCenterToken(center)));
        if (outside.length > 0) issues[index].push(`These centres are outside overall eligibility: ${outside.join(', ')}.`);
      }
    }

    const patientMode = String(window.patientRule.mode || 'INHERIT').toUpperCase();
    if (!['INHERIT', 'NEW_IN_WINDOW', 'NEW_REGISTRATION_RANGE', 'EXISTING', 'ANY_PERMITTED_OVERALL'].includes(patientMode)) {
      issues[index].push('Select a valid patient rule.');
    }
    const parentAllowsNew = ['ANY', 'NEW_REGISTRATION_RANGE', 'NEW_AND_EXISTING'].includes(context.overallPatientMode);
    const parentAllowsExisting = ['ANY', 'EXISTING_BEFORE', 'NEW_AND_EXISTING'].includes(context.overallPatientMode);
    if (['NEW_IN_WINDOW', 'NEW_REGISTRATION_RANGE'].includes(patientMode) && !parentAllowsNew) {
      issues[index].push('The overall patient rule does not permit new patients.');
    }
    if (patientMode === 'EXISTING' && !parentAllowsExisting) {
      issues[index].push('The overall patient rule does not permit existing patients.');
    }
    if (patientMode === 'NEW_REGISTRATION_RANGE') {
      const from = String(window.patientRule.registrationFrom || '');
      const to = String(window.patientRule.registrationTo || '');
      if (!validDateInput(from) || !validDateInput(to) || to < from) {
        issues[index].push('Enter a valid registration From and Through range.');
      } else {
        if (context.overallPatientMode === 'NEW_REGISTRATION_RANGE'
          && ((context.overallRegistrationFrom && from < context.overallRegistrationFrom)
          || (context.overallRegistrationTo && to > context.overallRegistrationTo))) {
          issues[index].push('Registration dates must remain inside the overall registration range.');
        }
        const windowFrom = Number.isFinite(start) ? istDateInput(start) : '';
        const windowTo = Number.isFinite(end) ? istDateInput(end) : '';
        if ((windowFrom && from < windowFrom) || (windowTo && to > windowTo)) {
          issues[index].push('Registration dates must remain inside this booking window.');
        }
      }
    }
    if (patientMode === 'NEW_IN_WINDOW' && context.overallPatientMode === 'NEW_REGISTRATION_RANGE'
      && Number.isFinite(start) && Number.isFinite(end)) {
      const from = istDateInput(start);
      const to = istDateInput(end);
      if ((context.overallRegistrationFrom && from < context.overallRegistrationFrom)
        || (context.overallRegistrationTo && to > context.overallRegistrationTo)) {
        issues[index].push('The booking-window registration dates must remain inside the overall registration range.');
      }
    }
    if (patientMode === 'EXISTING') {
      const cutoff = String(window.patientRule.existingRegisteredBefore || '');
      if (!validDateInput(cutoff)) {
        issues[index].push('Enter the existing-patient registered-before date.');
      } else if (context.overallPatientMode === 'EXISTING_BEFORE' && context.overallExistingBefore && cutoff > context.overallExistingBefore) {
        issues[index].push('The existing-patient cutoff cannot be later than the overall cutoff.');
      }
    }

    const advanceMode = String(window.advanceRule.mode || 'INHERIT').toUpperCase();
    if (!['INHERIT', 'EXACT_AMOUNT', 'MINIMUM_AMOUNT', 'RANGE', 'DISCOUNT_AMOUNT'].includes(advanceMode)) {
      issues[index].push('Select a valid advance rule.');
    } else if (['EXACT_AMOUNT', 'MINIMUM_AMOUNT'].includes(advanceMode) && !positiveClientAmount(window.advanceRule.amount)) {
      issues[index].push('Advance amount must be greater than zero.');
    } else if (advanceMode === 'RANGE') {
      const minimum = Number(window.advanceRule.minimumAmount);
      const maximum = Number(window.advanceRule.maximumAmount);
      if (!positiveClientAmount(minimum) || !positiveClientAmount(maximum) || minimum > maximum) {
        issues[index].push('Advance range requires positive amounts with minimum not greater than maximum.');
      }
    }
  });

  for (let left = 0; left < windows.length; left += 1) {
    for (let right = left + 1; right < windows.length; right += 1) {
      if (!Number.isFinite(parsed[left].startsAt) || !Number.isFinite(parsed[left].endsAt)
        || !Number.isFinite(parsed[right].startsAt) || !Number.isFinite(parsed[right].endsAt)) continue;
      if (parsed[left].startsAt <= parsed[right].endsAt && parsed[right].startsAt <= parsed[left].endsAt) {
        issues[left].push(`Overlaps ${windows[right].key || `Window ${right + 1}`}.`);
        issues[right].push(`Overlaps ${windows[left].key || `Window ${left + 1}`}.`);
      }
    }
  }
  return issues;
}

function normalizedCenterToken(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '');
}

function validDateInput(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function istDateInput(value: number): string {
  return toIstInput(new Date(value).toISOString()).slice(0, 10);
}

function positiveClientAmount(value: unknown): boolean {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0;
}

function formatWindowDate(value: string): string {
  return value ? `${formatOfferCampaignDateTime(value)} IST` : 'date pending';
}

function windowSummary(window: BookingWindowDraft, overallAdvanceRule: Record<string, unknown>): string {
  const centerSummary = window.centerRule.mode === 'SUBSET'
    ? `${Array.isArray(window.centerRule.eligibleCenters) ? window.centerRule.eligibleCenters.length : 0} centres`
    : 'overall centres';
  return `${formatWindowDate(window.startsAt)} to ${formatWindowDate(window.endsAt)} · ${centerSummary} · ${windowEffectiveSummary(window, overallAdvanceRule)}`;
}

function windowEffectiveSummary(window: BookingWindowDraft, overallAdvanceRule: Record<string, unknown>): string {
  const patient = String(window.patientRule.mode || 'INHERIT').replaceAll('_', ' ').toLowerCase();
  const advance = window.advanceRule.mode === 'INHERIT' ? overallAdvanceRule : window.advanceRule;
  const advanceMode = String(advance.mode || advance.type || 'not configured').replaceAll('_', ' ').toLowerCase();
  const amount = Number(advance.amount || 0);
  const range = String(advance.mode || advance.type) === 'RANGE'
    ? ` ₹${Number(advance.minimumAmount || 0).toLocaleString('en-IN')}–₹${Number(advance.maximumAmount || 0).toLocaleString('en-IN')}`
    : amount > 0 ? ` ₹${amount.toLocaleString('en-IN')}` : '';
  return `${patient}; ${advanceMode}${range}`;
}

function CenterChecklistField({ label, values, options, disabled = false, onChange }: { label: string; values: string[]; options: string[]; disabled?: boolean; onChange: (values: string[]) => void }) {
  const cleanedOptions = [...new Set([...options, ...values].map((option) => String(option || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const selected = new Set(values.map((value) => String(value || '').trim()).filter(Boolean));
  const selectedValues = cleanedOptions.filter((option) => selected.has(option));
  const allSelected = cleanedOptions.length > 0 && selectedValues.length === cleanedOptions.length;
  const toggleOption = (option: string, checked: boolean) => {
    const next = new Set(selectedValues);
    if (checked) next.add(option);
    else next.delete(option);
    onChange(cleanedOptions.filter((item) => next.has(item)));
  };

  return (
    <div className="offer-center-checklist-field">
      <span>{label}</span>
      <p className="offer-center-checklist-help">Leave empty to allow all centers, or select specific eligible centers.</p>
      <div className="offer-center-checklist-box">
        <label className="offer-center-checklist-select-all">
          <input
            type="checkbox"
            checked={allSelected}
            disabled={disabled || cleanedOptions.length === 0}
            onChange={(event) => onChange(event.target.checked ? cleanedOptions : [])}
          />
          <span>Select All</span>
          <small>{selectedValues.length ? `${selectedValues.length} selected` : 'All centers allowed when blank'}</small>
        </label>
        {cleanedOptions.length ? (
          <div className="offer-center-checklist-list">
            {cleanedOptions.map((option) => (
              <label key={option} className="offer-center-checklist-item" title={option}>
                <input
                  type="checkbox"
                  checked={selected.has(option)}
                  disabled={disabled}
                  onChange={(event) => toggleOption(option, event.target.checked)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="offer-center-checklist-empty">No centers loaded yet. Save is still allowed; blank means all centers.</p>
        )}
      </div>
    </div>
  );
}

function emptyDraft(): OfferCampaignAdminPayload & { campaignKey?: string } {
  return {
    campaignKey: '',
    campaignName: '',
    routeSlug: '',
    displayRouteSlug: '',
    status: 'DRAFT',
    startsAt: null,
    endsAt: null,
    totalSlotLimit: 0,
    discountFormulaType: 'AGE_MULTIPLIER',
    ageMultiplier: 0,
    minAge: null,
    maxAge: null,
    eligibleCenters: [],
    codePattern: '{CAMPAIGN_KEY}-{CENTER_CODE}-{YYYYMMDD}-{SEQ}',
    codeSequenceScope: 'CENTER_DAY',
    reservationTtlSeconds: 900,
    displayRefreshSeconds: 10,
    celebrationEffect: 'SPARKLE_CONFETTI',
    themeConfig: {
      description: '',
      sequencePadding: 3,
      oneMrnOnce: true,
      unlimitedSlots: false,
      requiredAdvanceAmountRule: { type: 'DISCOUNT_AMOUNT' },
      reservationsConsumeSlots: true,
      showLeaderboard: true,
      showCenterCards: true,
      showRemainingSlots: true,
    },
    sources: [
      { sourceType: 'PATIENT_REGISTRATION', lookerLookId: '', sourceName: 'Patient Registration Source', fieldMappings: {}, requiredFilters: {}, timezone: 'Asia/Kolkata', isActive: false },
      { sourceType: 'ADVANCE_PAYMENT', lookerLookId: '', sourceName: 'Advance Payment Source', fieldMappings: {}, requiredFilters: {}, timezone: 'Asia/Kolkata', isActive: false },
    ],
  };
}

function draftFromCampaign(row: OfferCampaignAdminSummary): OfferCampaignAdminPayload & { campaignKey?: string } {
  return {
    ...emptyDraft(),
    ...row,
    ageMultiplier: Number(row.ageMultiplier || 0),
    eligibleCenters: Array.isArray(row.eligibleCenters) ? row.eligibleCenters : [],
    themeConfig: {
      ...emptyDraft().themeConfig,
      ...(row.themeConfig || {}),
      oneMrnOnce: row.themeConfig?.oneMrnOnce !== false,
      unlimitedSlots: row.themeConfig?.unlimitedSlots === true,
    },
    sources: sourceTypes.map((type) => {
      const source = row.sources.find((item) => item.sourceType === type);
      return {
        sourceType: type,
        lookerLookId: String(source?.lookerLookId || ''),
        sourceName: source?.sourceName || (type === 'PATIENT_REGISTRATION' ? 'Patient Registration Look' : 'Advance Payment Look'),
        fieldMappings: source?.fieldMappings || {},
        requiredFilters: source?.requiredFilters || {},
        timezone: source?.timezone || 'Asia/Kolkata',
        isActive: source?.isActive ?? false,
      };
    }),
  };
}

function normalizeDraft(draft: OfferCampaignAdminPayload & { campaignKey?: string }): OfferCampaignAdminPayload {
  const themeConfig = normalizeThemeConfigForPersistence(draft.themeConfig);
  return {
    campaignName: draft.campaignName.trim(),
    routeSlug: draft.routeSlug.trim(),
    displayRouteSlug: draft.displayRouteSlug.trim(),
    status: draft.status,
    startsAt: draft.startsAt || null,
    endsAt: draft.endsAt || null,
    totalSlotLimit: draft.themeConfig?.unlimitedSlots === true
      ? Math.max(1, Number(draft.totalSlotLimit) || 1)
      : Number(draft.totalSlotLimit || 0),
    discountFormulaType: draft.discountFormulaType,
    ageMultiplier: Number(draft.ageMultiplier || 0),
    minAge: draft.minAge === null || draft.minAge === undefined ? null : Number(draft.minAge),
    maxAge: draft.maxAge === null || draft.maxAge === undefined ? null : Number(draft.maxAge),
    eligibleCenters: Array.isArray(draft.eligibleCenters)
      ? [...new Set(draft.eligibleCenters.map((center) => String(center || '').trim()).filter(Boolean))]
      : [],
    codePattern: draft.codePattern.trim(),
    codeSequenceScope: draft.codeSequenceScope,
    reservationTtlSeconds: Number(draft.reservationTtlSeconds || 1),
    displayRefreshSeconds: Number(draft.displayRefreshSeconds || 1),
    celebrationEffect: draft.celebrationEffect,
    themeConfig: {
      ...themeConfig,
      oneMrnOnce: draft.themeConfig?.oneMrnOnce !== false,
      unlimitedSlots: draft.themeConfig?.unlimitedSlots === true,
    },
    sources: sourceTypes.map((type) => {
      const source = sourceFor(draft, type);
      return {
        sourceType: source.sourceType,
        lookerLookId: String(source.lookerLookId || ''),
        sourceName: String(source.sourceName || source.sourceType).trim(),
        fieldMappings: normalizeObject(source.fieldMappings),
        requiredFilters: normalizeObject(source.requiredFilters),
        timezone: String(source.timezone || 'Asia/Kolkata').trim(),
        isActive: source.isActive === true,
      };
    }),
  };
}

function normalizeThemeConfigForPersistence(value: unknown): Record<string, unknown> {
  const themeConfig = { ...normalizeObject(value) };
  const couponValidityUnit = String(themeConfig.couponValidityUnit || 'DAYS').trim().toUpperCase();
  themeConfig.couponValidityUnit = couponValidityUnit;
  if (couponValidityUnit === 'FIXED_DATE') {
    delete themeConfig.couponValidityDuration;
  } else {
    delete themeConfig.couponValidityFixedAt;
  }
  return themeConfig;
}

function campaignDraftFingerprint(draft: OfferCampaignAdminPayload & { campaignKey?: string }): string {
  return stableJson({
    campaignKey: String(draft.campaignKey || '').trim().toUpperCase(),
    ...normalizeDraft(draft),
  });
}

function mergeCampaignPayload(
  baseline: OfferCampaignAdminPayload,
  requested: OfferCampaignAdminPayload,
  latest: OfferCampaignAdminPayload,
): { value: OfferCampaignAdminPayload; conflicts: string[] } {
  const result = mergeCampaignValue(baseline, requested, latest, '');
  return {
    value: result.value as OfferCampaignAdminPayload,
    conflicts: result.conflicts,
  };
}

function mergeCampaignValue(
  baseline: unknown,
  requested: unknown,
  latest: unknown,
  path: string,
): { value: unknown; conflicts: string[] } {
  if (stableJson(requested) === stableJson(baseline)) return { value: latest, conflicts: [] };
  if (stableJson(latest) === stableJson(baseline) || stableJson(requested) === stableJson(latest)) {
    return { value: requested, conflicts: [] };
  }

  if (isPlainRecord(baseline) && isPlainRecord(requested) && isPlainRecord(latest)) {
    const keys = new Set([...Object.keys(baseline), ...Object.keys(requested), ...Object.keys(latest)]);
    const value: Record<string, unknown> = {};
    const conflicts: string[] = [];
    for (const key of keys) {
      const childPath = path ? `${path}.${key}` : key;
      const child = mergeCampaignValue(baseline[key], requested[key], latest[key], childPath);
      if (child.value !== undefined) value[key] = child.value;
      conflicts.push(...child.conflicts);
    }
    return { value, conflicts };
  }

  return { value: requested, conflicts: [path || 'campaign configuration'] };
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function campaignPersistenceIssues(expected: OfferCampaignAdminPayload, actual: OfferCampaignAdminSummary): string[] {
  const issues: string[] = [];
  const expectedPersisted = normalizeDraft(expected);
  const actualPersisted = normalizeDraft(draftFromCampaign(actual));
  const scalarFields: Array<[keyof OfferCampaignAdminPayload, string]> = [
    ['campaignName', 'Workflow Name'],
    ['routeSlug', 'Enrollment Route Slug'],
    ['displayRouteSlug', 'Display Route Slug'],
    ['status', 'Status'],
    ['totalSlotLimit', 'Total Slot Limit'],
    ['discountFormulaType', 'Discount Formula'],
    ['minAge', 'Min Age'],
    ['maxAge', 'Max Age'],
    ['codePattern', 'Code Pattern'],
    ['codeSequenceScope', 'Sequence Scope'],
    ['reservationTtlSeconds', 'Reservation TTL Seconds'],
    ['displayRefreshSeconds', 'Display Refresh Seconds'],
    ['celebrationEffect', 'Celebration Effect'],
  ];
  for (const [key, label] of scalarFields) {
    if (stableJson(expectedPersisted[key] ?? null) !== stableJson(actualPersisted[key] ?? null)) {
      issues.push(`${label} was not persisted`);
    }
  }
  for (const [key, label] of [['startsAt', 'Start Date/Time'], ['endsAt', 'End Date/Time']] as const) {
    if (normalizedIsoDate(expectedPersisted[key]) !== normalizedIsoDate(actualPersisted[key])) issues.push(`${label} was not persisted`);
  }
  if (stableJson(expectedPersisted.eligibleCenters || []) !== stableJson(actualPersisted.eligibleCenters || [])) {
    issues.push('Eligible Centers were not persisted');
  }
  if (stableJson(expectedPersisted.themeConfig || {}) !== stableJson(actualPersisted.themeConfig || {})) {
    issues.push('one or more workflow settings in steps 1, 4, 5, 6, 7 or 8 were not persisted');
  }

  const expectedSources = (expectedPersisted.sources || [])
    .filter((source) => source.isActive === true || source.lookerLookId.trim() !== '')
    .map(persistedSourceValue)
    .sort((left, right) => left.sourceType.localeCompare(right.sourceType));
  const actualSources = (actualPersisted.sources || [])
    .filter((source) => source.isActive === true || source.lookerLookId.trim() !== '')
    .map(persistedSourceValue)
    .sort((left, right) => left.sourceType.localeCompare(right.sourceType));
  if (stableJson(expectedSources) !== stableJson(actualSources)) {
    issues.push('Looker Sources or Field Mapping values were not persisted');
  }
  return issues;
}

function persistedSourceValue(source: {
  sourceType: string;
  lookerLookId?: string;
  sourceName?: string;
  fieldMappings?: Record<string, unknown>;
  requiredFilters?: Record<string, unknown>;
  timezone?: string;
  isActive?: boolean;
}) {
  return {
    sourceType: source.sourceType,
    lookerLookId: String(source.lookerLookId || '').trim(),
    sourceName: String(source.sourceName || source.sourceType).trim(),
    fieldMappings: normalizeObject(source.fieldMappings),
    requiredFilters: normalizeObject(source.requiredFilters),
    timezone: String(source.timezone || 'Asia/Kolkata').trim(),
    isActive: source.isActive === true,
  };
}

function stableJson(value: unknown): string {
  return JSON.stringify(sortJsonValue(value));
}

function sortJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortJsonValue);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, sortJsonValue(item)]),
  );
}

function normalizedIsoDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function sourceFor(draft: OfferCampaignAdminPayload, type: 'PATIENT_REGISTRATION' | 'ADVANCE_PAYMENT'): OfferCampaignAdminSourcePayload {
  const source = draft.sources?.find((item) => item.sourceType === type);
  return source || { sourceType: type, lookerLookId: '', sourceName: type, fieldMappings: {}, requiredFilters: {}, timezone: 'Asia/Kolkata', isActive: false };
}

function updateSource(draft: OfferCampaignAdminPayload, onChange: (draft: OfferCampaignAdminPayload) => void, type: 'PATIENT_REGISTRATION' | 'ADVANCE_PAYMENT', patch: Partial<OfferCampaignAdminSourcePayload>) {
  const next = sourceTypes.map((sourceType) => sourceType === type ? { ...sourceFor(draft, type), ...patch } : sourceFor(draft, sourceType));
  onChange({ ...draft, sources: next });
}

function eligibleCenterOptions(draft: OfferCampaignAdminPayload, centers: string[]): string[] {
  const configured = draft.eligibleCenters || [];
  return [...new Set([...centers, ...configured].map((item) => String(item || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function parseJsonObject(value: string): Record<string, unknown> {
  const parsed = JSON.parse(value || '{}');
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Enter a valid JSON object, for example { "Center": "5001" }.');
  }
  return parsed as Record<string, unknown>;
}

function normalizeObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

/** `datetime-local` has no timezone. Analytics scheduling is explicitly IST. */
function toIstInput(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date).reduce<Record<string, string>>((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function istInputToIso(value: string) {
  return new Date(`${value}:00+05:30`).toISOString();
}

function formatIstDateTime(value: string | null | undefined): string {
  return value ? formatOfferCampaignDateTime(value) : 'not set';
}

function errorMessage(error: unknown) {
  const payload = error && typeof error === 'object' && 'payload' in error ? (error as { payload?: unknown }).payload : null;
  const message = payload && typeof payload === 'object' && 'message' in payload ? (payload as { message?: unknown }).message : null;
  if (Array.isArray(message)) return message.join('; ');
  if (typeof message === 'string' && message.trim()) return message;
  if (error instanceof Error) return error.message;
  return String(error || 'Request failed');
}

function errorIssues(error: unknown) {
  const payload = error && typeof error === 'object' && 'payload' in error ? (error as { payload?: unknown }).payload : null;
  const message = payload && typeof payload === 'object' && 'message' in payload ? (payload as { message?: unknown }).message : null;
  const rawItems = Array.isArray(message)
    ? message
    : [typeof message === 'string' && message.trim() ? message : errorMessage(error)];
  const issues = rawItems
    .flatMap((item) => String(item).split(';'))
    .map((item) => item.trim())
    .filter(Boolean);
  return issues.length > 0 ? issues : ['Save failed. Please review the workflow configuration and try again.'];
}
